import * as React from "react"
import { AnimatePresence, animate, motion, useMotionValue, useTransform } from "framer-motion"
import type { MotionValue } from "framer-motion"
import { C, FONT, R, S, SITE_BG, EASE, SPRING, T, BP, GLASS_CSS } from "./tokens"
import { Press, CountUp, Skeleton, Toast, Icon, IconName, useReduced } from "./prims"
import {
    PAYVIT_ACCOUNT, Tx, STATUS,
    balanceUsd as balanceUsdFixture, vitCount as vitCountFixture, vitProgress as vitProgressFixture,
    fmtUSD, fmtHTG, fmt, calcFee,
    RATE_DEPO as RATE_DEPO_FIXTURE, RATE_RETRE as RATE_RETRE_FIXTURE, VIT_USD_RATE,
} from "./data"
import { CSS } from "./styles"
import { Avatar } from "./avatar"
import { PayvitLogo } from "./logo"
import { useSession, LoginScreen, SessionLoadingScreen } from "./auth"
import {
    fetchServiceStatus, fetchBalanceUsd, fetchHistory, fetchPendingDeposit, loadCachedServiceStatus,
    submitWithdrawal, submitRemittance, submitBridgeTransfer, submitDeposit, submitInterne,
    fetchCardQuote, submitCardCreate, fetchCardStatus, fetchCardDetails, submitCardFreeze,
    fetchCardTopupQuote, submitCardTopup,
    generateIdempotencyKey, ServiceStatus, VitBalance, PendingDeposit, CardQuote, CardStatus, CardTopupQuote,
    fetchRecipients, submitRecipient, RemoteRecipient,
} from "./lib/api"
import { enablePush, disablePush, isCurrentlySubscribed, isPushSupported, clearAppBadge } from "./lib/push"
import { downloadReceipt, downloadReceiptPdf, shareReceipt } from "./lib/receipt"

type TabKey = "akey" | "transfe" | "kat" | "istorik" | "profil" | "rechaje" | "notif"
/** Order is the BAR order, not the information hierarchy: home sits in the
 *  centre slot because that is where the raised button goes. Two items either
 *  side keeps it symmetric. */
const TABS: { key: TabKey; label: string; icon: IconName }[] = [
    { key: "transfe", label: "Transfè", icon: "swap" },
    { key: "kat", label: "Kat", icon: "card" },
    { key: "akey", label: "Akèy", icon: "home" },
    { key: "istorik", label: "Istorik", icon: "clock" },
    { key: "profil", label: "Profil", icon: "user" },
]
/** The rail keeps reading order — a vertical list has no centre slot to
 *  privilege, so home goes back to the top where a sidebar expects it. */
const RAIL_TABS: TabKey[] = ["akey", "transfe", "kat", "istorik", "profil"]
/** Not in the bar — reached from the balance card, but it still needs a
 *  header icon like every other screen. */
const EXTRA_ICON: Partial<Record<TabKey, IconName>> = { rechaje: "wallet", notif: "bell" }
const CENTER: TabKey = "akey"

/** First-open only. Checked against the auth flow: the app already persists a
 *  session under `payvit-auth-token`, so a returning user is one who has a
 *  session — gating on a separate key means someone who signs out and back in
 *  is not shown the threshold again either. Per-session would fire on every
 *  cold start, which on mobile is often, and that turns a welcome into a toll
 *  gate. */
const INTRO_KEY = "payvit-intro-seen"
/** PREVIEW SWITCH — `true` in production, so the welcome is first-open-only.
 *  `false` here so the screen can actually be reviewed: the published preview
 *  runs inside an iframe, which makes both a URL hash and a cleared
 *  localStorage awkward to reach from the outside. Flip to `true` when porting
 *  to the live app; nothing else changes. */
const INTRO_REMEMBER = true

/** FEATURE SWITCH — the WaldenPay virtual card flow works end-to-end
 *  against the real backend, but isn't ready for the public launch (still
 *  being tested; the backend side has uncommitted in-progress changes as
 *  of this writing). While `false`, the Kat tab shows a "coming soon"
 *  placeholder instead — see `Kat()` below — and makes no card API calls
 *  at all. Flip to `true` once the card flow is confirmed ready; nothing
 *  else needs to change. */
const CARD_FEATURE_LIVE = false

/** DRAFT — the SAME mechanism as TransfeFlow.tsx, not a second one.
 *  Same key shape (`payvit-tf-draft:<userId>`), same version gate, same
 *  seven-day TTL. They must interoperate: a draft written by the live flow
 *  has to be readable here, or a user is offered a resume that restores
 *  nothing. What changed is only WHEN it is written — see `snapshot`. */
const DRAFT_VERSION = 1
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000
const draftKey = (userId: string) => `payvit-tf-draft:${userId || "anon"}`

export interface TransferDraft {
    v: number; savedAt: number; step: number; mode: string | null
    direction: string; rail: string; intl: string
    amount: string; note: string; recipientId: string | null
}
function loadDraft(userId: string): TransferDraft | null {
    try {
        const raw = localStorage.getItem(draftKey(userId))
        if (!raw) return null
        const d = JSON.parse(raw) as TransferDraft
        if (d?.v !== DRAFT_VERSION) return null
        if (!d.savedAt || Date.now() - d.savedAt > DRAFT_TTL_MS) {
            localStorage.removeItem(draftKey(userId)); return null
        }
        return d
    } catch (_e) { return null }
}
const saveDraft = (u: string, d: TransferDraft) => {
    try { localStorage.setItem(draftKey(u), JSON.stringify(d)) } catch (_e) {}
}
const clearDraft = (u: string) => {
    try { localStorage.removeItem(draftKey(u)) } catch (_e) {}
}
function agoKreyol(ts: number): string {
    const s = Math.max(0, Math.floor((Date.now() - ts) / 1000))
    if (s < 60) return "kounye a"
    const m = Math.floor(s / 60); if (m < 60) return `gen ${m} minit`
    const h = Math.floor(m / 60); if (h < 24) return `gen ${h} è`
    const d = Math.floor(h / 24); return d === 1 ? "gen 1 jou" : `gen ${d} jou`
}

/** ANNOUNCEMENTS — admin-published, so this is the contract the backend has
 *  to satisfy. Flagged in the notes: id, kind, title, body, at, optional url. */
export interface Announce {
    id: string
    kind: "update" | "promo" | "maintenance"
    title: string
    body: string
    at: string
    url?: string
}
const ANNOUNCEMENTS: Announce[] = [
    { id: "a1", kind: "promo", title: "Frè redwi sou Depo", at: "Jodi a",
      body: "Jiska 31 out, tout depo anba $50 gen yon frè $3.00 sèlman." },
    { id: "a2", kind: "update", title: "Kat vityèl la disponib", at: "Yè",
      body: "Ou ka kounye a mande yon kat vityèl Payvit depi sou tablodbò w la." },
    { id: "a3", kind: "maintenance", title: "Antretyen NatCash", at: "26 jiyè",
      body: "NatCash ap fè antretyen dimanch 3 out, 1è–4è nan maten." },
]
const TAB_TITLE: Record<TabKey, string> = {
    akey: "Akèy", transfe: "Tranzaksyon", kat: "Kat vityèl mwen", istorik: "Istorik", profil: "Profil",
    rechaje: "Rechaje Balans", notif: "Notifikasyon",
}

export default function App() {
    const reduced = useReduced()
    const session = useSession()
    const userId = session.user?.id ?? "anon"

    /** Live overrides — fixture values are the initial-render fallback until
     *  the real endpoints resolve, and stay the fallback forever if a call
     *  fails. Named to shadow the fixture imports, so every existing
     *  reference below (`RATE_DEPO`, `balanceUsd`, `vitCount`, `vitProgress`)
     *  picks up the live value with no other call sites touched. */
    const [liveRates, setLiveRates] = React.useState<ServiceStatus | null>(() => loadCachedServiceStatus())
    const [liveBalance, setLiveBalance] = React.useState<{ usd: number | null; vit: VitBalance | null }>({ usd: null, vit: null })
    const [pendingDeposit, setPendingDeposit] = React.useState<PendingDeposit | null>(null)
    const RATE_DEPO = liveRates?.taux_htg_usd ?? RATE_DEPO_FIXTURE
    const RATE_RETRE = liveRates?.taux_usd_htg ?? RATE_RETRE_FIXTURE
    const balanceUsd = liveBalance.usd ?? balanceUsdFixture
    const vitCount = liveBalance.vit?.count ?? vitCountFixture
    const vitProgress = liveBalance.vit?.progress ?? vitProgressFixture

    /** Replaces the `USER` fixture everywhere it was previously hardcoded to
     *  "Audemar Laurent" regardless of who was actually logged in. `phone`
     *  has no live source anywhere (not the JWT, not /client/balance, not
     *  /client/history) — stays `null` and every reader shows a placeholder
     *  rather than a fabricated number. Where a phone is functionally
     *  required for a submission (Bridge Lokal's `sender_phone`), the user
     *  types it in directly on that screen instead. */
    const currentUser = {
        seed: session.user?.id ?? "guest",
        name: session.user?.name ?? session.user?.email?.split("@")[0] ?? "Itilizatè",
        email: session.user?.email ?? "—",
        phone: null as string | null,
    }

    /** Fire-and-forget — balance never updates optimistically, matching the
     *  real app exactly (the row just submitted is still `en_attente`, so a
     *  refetched balance may not reflect it yet either; that's expected).
     *  Called once on login and again after every successful submission. */
    const refreshBalanceAndHistory = React.useCallback(() => {
        if (!session.user) return Promise.resolve()
        fetchServiceStatus().then((s) => { if (s) setLiveRates(s) })
        return session.getAccessToken().then((token) => {
            if (!token) return
            fetchPendingDeposit(token).then(setPendingDeposit)
            return Promise.all([
                fetchBalanceUsd(session.user!.email, token).then(setLiveBalance),
                fetchHistory(token).then((rows) => { if (rows) setTxs(rows) }),
            ])
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session.user?.id])

    React.useEffect(() => {
        if (session.status !== "authenticated") return
        let live = true
        // `loading` now tracks whether THIS user's real balance/history has
        // actually arrived — not a fixed timer. A real account's own numbers
        // must never render before they're known to be real, and a fixed
        // delay can't guarantee that on a slow connection.
        setLoading(true)
        refreshBalanceAndHistory().finally(() => { if (live) setLoading(false) })
        return () => { live = false }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session.status, session.user?.id])

    const [intro, setIntro] = React.useState<boolean | null>(null)
    React.useEffect(() => {
        if (!INTRO_REMEMBER) { setIntro(true); return }
        try { setIntro(!localStorage.getItem(INTRO_KEY)) } catch (_e) { setIntro(false) }
    }, [])
    /** Drives the dashboard's entrance. Set when the intro STARTS leaving, so
     *  the two overlap — the app rises into place THROUGH the fade rather than
     *  appearing, fully formed, once the fade is over. */
    const [entered, setEntered] = React.useState(false)
    /** The page mounts BEHIND the intro, so an entrance keyed to its mount
     *  plays to nobody and is over before the intro lifts. It is keyed to
     *  `entered` instead: held at the pre-entrance pose while the intro is up,
     *  released when the intro starts leaving. `pageArrived` then hands the
     *  element back to its ordinary per-tab slide. */
    const [pageArrived, setPageArrived] = React.useState(false)
    const dismissIntro = () => {
        if (INTRO_REMEMBER) {
            try { localStorage.setItem(INTRO_KEY, "1") } catch (_e) {}
        }
        setIntro(false)
    }

    const [tab, setTab] = React.useState<TabKey>("akey")
    const [dir, setDir] = React.useState(1)
    const [detail, setDetail] = React.useState<Tx | null>(null)
    const [toast, setToast] = React.useState<string | null>(null)
    /** The transaction the processing popup is tracking, and the one the
     *  dashboard should highlight when the user follows it. */
    const [processing, setProcessing] = React.useState<Tx | null>(null)
    /** Live list — starts EMPTY, never the fixture array. A real logged-in
     *  user must never see fixture people's names (even briefly, while the
     *  real history call is in flight) on what looks like their own account;
     *  `loading` (tied to the real fetch below, not a timer) gates the UI
     *  until this is actually populated. A submitted transaction is
     *  prepended immediately as `en_attente` — otherwise "Swiv Tranzaksyon"
     *  has no row to scroll to, and the user lands on a dashboard that does
     *  not yet know about the thing they just did. */
    const [txs, setTxs] = React.useState<Tx[]>([])
    /** No read/unread tracking exists anywhere (no backend concept of it,
     *  nothing stored locally either) — so rather than a dot that's always
     *  on regardless of whether anything's actually happened, it reflects
     *  whether there's currently something genuinely worth a look: a
     *  transaction still awaiting confirmation, one that got rejected, or a
     *  low balance. Matches exactly what `Notifikasyon` itself would show. */
    const hasUrgentNotif = (balanceUsd > 0 && balanceUsd < 50)
        || txs.some((t) => t.statut === "en_attente" || t.statut === "rejete")
    const [draft, setDraft] = React.useState<TransferDraft | null>(null)
    const [draftChecked, setDraftChecked] = React.useState(false)
    /** Credited by Rechaje. Production re-derives from the settled rows. */
    const [topup, setTopup] = React.useState(0)
    /** Manual avatar override, the control specified when name-based gendered
     *  avatars were first built and never given a UI. "auto" defers to
     *  inferPresentation; anything else wins over it. */
    const [avatarPref, setAvatarPref] = React.useState<"auto" | "feminine" | "masculine" | "neutral">("auto")

    /** Read once per logged-in user — which is exactly what covers an
     *  accidental close (closing the tab is an unmount that never came back,
     *  so the next mount IS the relaunch) AND covers switching accounts
     *  (re-runs when userId changes, so account B is never offered account
     *  A's draft). */
    React.useEffect(() => {
        const d = loadDraft(userId)
        setDraft(d && (d.step > 0 || d.amount) ? d : null)
        setDraftChecked(true)
    }, [userId])
    const [highlight, setHighlight] = React.useState<string | null>(null)
    const [loading, setLoading] = React.useState(true)
    const [hidden, setHidden] = React.useState(false)
    const [cur, setCur] = React.useState<"USD" | "HTG">("USD")
    const scrollRef = React.useRef<HTMLDivElement | null>(null)

    const say = (m: string) => {
        setToast(m)
        window.setTimeout(() => setToast(null), 1900)
    }

    /** One place changes the tab, so direction and scroll reset can never
     *  disagree with each other — the shell stays put, only the page moves. */
    const go = (next: TabKey) => {
        if (next === tab) return
        setDir(TABS.findIndex((t) => t.key === next) >= TABS.findIndex((t) => t.key === tab) ? 1 : -1)
        setTab(next)
        scrollRef.current?.scrollTo({ top: 0, behavior: reduced ? "auto" : "smooth" })
    }

    /** "Swiv Tranzaksyon" — close, go home, find the row, open it.
     *
     *  Ordered deliberately: the tab switch has to commit and the list has to
     *  paint before the row exists to scroll to, so the scroll and the sheet
     *  wait a frame rather than racing the render. */
    const followTx = (tx: Tx) => {
        setProcessing(null)
        setHighlight(tx.id)
        go("akey")
        window.setTimeout(() => {
            const el = document.querySelector(`[data-tx="${tx.id}"]`)
            el?.scrollIntoView({ block: "center", behavior: reduced ? "auto" : "smooth" })
            window.setTimeout(() => setDetail(tx), reduced ? 0 : 420)
            window.setTimeout(() => setHighlight(null), 2600)
        }, reduced ? 0 : 380)
    }

    /** True while the intro still owns the screen and has not begun to leave. */
    const pageHidden = !!intro && !entered

    const money = (usd: number) =>
        hidden ? "••••••" : cur === "USD" ? fmtUSD(usd) : fmtHTG(usd * RATE_DEPO)

    return (
        <div className="app" style={{ background: SITE_BG }}>
            <style>{GLASS_CSS + CSS}</style>
            {session.status === "loading" ? (
                <SessionLoadingScreen />
            ) : session.status === "unauthenticated" || session.recovering ? (
                <LoginScreen session={session} />
            ) : (
            <>
            <div className="ambient" aria-hidden />
            {intro && <Intro onStart={dismissIntro} onLeaveStart={() => setEntered(true)} />}

            {/* ══ PERSISTENT SHELL ══════════════════════════════════════════
                One header, one nav. The page swaps underneath them; neither
                unmounts, so nothing about the chrome flickers between screens.
                This is the single biggest difference from the current build,
                where each page draws its own header. */}
            {/* The shell arrives in reading order: bar, then sidebar, then
                content. NOTE the animation is on each PART, never on .shell —
                .shell is the ancestor of the position:fixed header, and any
                transform there would make it the containing block and pull the
                full-bleed bar back inside the content column. */}
            <div className="shell">
                <motion.aside className="rail g g-card"
                    initial={false}
                    animate={entered || !intro
                        ? { opacity: 1, x: 0 }
                        : reduced ? { opacity: 0 } : { opacity: 0, x: -14 }}
                    transition={reduced ? { duration: 0.2 } : { ...T.slow, delay: 0.06 }}>
                    <div className="rail-brand"><PayvitLogo size={26} /><span>Payvit</span></div>
                    <nav className="rail-nav" aria-label="Navigasyon prensipal">
                        {RAIL_TABS.map((k) => TABS.find((x) => x.key === k)!).map((t) => (
                            <Press
                                key={t.key}
                                className={`rail-item${tab === t.key ? " on" : ""}`}
                                onClick={() => go(t.key)}
                                aria-current={tab === t.key ? "page" : undefined}
                            >
                                {tab === t.key && (
                                    <motion.span layoutId="rail-pill" className="rail-pill" transition={SPRING} />
                                )}
                                <span className="rail-in">
                                    <Icon name={t.icon} size={20} stroke={tab === t.key ? 2 : 1.5} />
                                    {t.label}
                                </span>
                            </Press>
                        ))}
                    </nav>
                    <div className="rail-foot">
                        <Avatar seed={currentUser.seed} name={currentUser.name} size={34} />
                        <div style={{ minWidth: 0 }}>
                            <div className="rail-nm">{currentUser.name}</div>
                            <div className="rail-em">{currentUser.email}</div>
                        </div>
                        <Press className="ico g g-chip" onClick={() => session.signOut()} aria-label="Dekonekte" style={{ marginLeft: "auto" }}>
                            <Icon name="arrowRight" size={16} color={C.textMute} />
                        </Press>
                    </div>
                </motion.aside>

                <div className="col">
                    <motion.header className="hdr"
                        initial={false}
                        animate={entered || !intro
                            ? { opacity: 1, y: 0 }
                            : reduced ? { opacity: 0 } : { opacity: 0, y: -10 }}
                        transition={reduced ? { duration: 0.2 } : T.slow}>
                        <div className="hdr-l">
                            {/* Cross-fades with the title, on the same
                                timing, so the two read as one label changing
                                rather than two things updating. */}
                            <span className="hdr-mark g g-chip">
                                <AnimatePresence mode="wait" initial={false}>
                                    <motion.span
                                        key={tab}
                                        initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.82 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.82 }}
                                        transition={T.quick}
                                        style={{ display: "flex" }}
                                    >
                                        <Icon
                                            name={EXTRA_ICON[tab] ?? TABS.find((t) => t.key === tab)?.icon ?? "home"}
                                            size={20}
                                            color={C.tealDeep}
                                            stroke={1.9}
                                        />
                                    </motion.span>
                                </AnimatePresence>
                            </span>
                            {/* The title cross-fades; the bar does not move. */}
                            <AnimatePresence mode="wait" initial={false}>
                                <motion.h1
                                    key={tab}
                                    className="hdr-t"
                                    initial={reduced ? { opacity: 0 } : { opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={reduced ? { opacity: 0 } : { opacity: 0, y: -6 }}
                                    transition={T.quick}
                                >
                                    {TAB_TITLE[tab]}
                                </motion.h1>
                            </AnimatePresence>
                        </div>
                        <div className="hdr-r">
                            {/* The bell leaves entirely on its own page — a
                                button that takes you where you already are is
                                just clutter. It collapses its width rather
                                than vanishing, and the negative margin cancels
                                the flex gap it would otherwise leave behind,
                                so the avatar slides over instead of jumping. */}
                            <AnimatePresence initial={false}>
                                {tab !== "notif" && (
                                    <motion.div className="notif-wrap"
                                        initial={reduced ? { opacity: 0 } : { opacity: 0, width: 0, marginRight: -S[2] }}
                                        animate={{ opacity: 1, width: 44, marginRight: 0 }}
                                        exit={reduced ? { opacity: 0 } : { opacity: 0, width: 0, marginRight: -S[2] }}
                                        transition={T.quick}>
                                        <Press className="ico g g-chip" onClick={() => go("notif")}
                                               aria-label="Notifikasyon">
                                            <Icon name="bell" size={21} color={C.text} />
                                            {hasUrgentNotif && <span className="notif-dot" aria-hidden />}
                                        </Press>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                            <Press className="ico g g-chip" onClick={() => go("profil")} aria-label="Profil">
                                <Avatar seed={currentUser.seed} name={currentUser.name} size={32} />
                            </Press>
                        </div>
                    </motion.header>

                    <div className="scroll" ref={scrollRef}>
                        {/* Keyed remount, NOT AnimatePresence mode="wait".
                        
                            mode="wait" holds the incoming page until the
                            outgoing one finishes exiting — and a `layoutId`
                            still animating inside the outgoing tree never
                            reports finished, because the element running it is
                            being removed by the transition that is waiting for
                            it. Leave the transfer flow mid-step and the next
                            tab renders blank, permanently.
                        
                            Remounting on `tab` has no exit to wait on. It is
                            also closer to how a native shell behaves: the
                            chrome persists and the content is replaced. */}
                        {/* Two entrances, one element. While the intro is up the
                            page is held below and slightly small; when the intro
                            starts leaving it rises into place. Every tab change
                            after that is the lateral slide it has always been. */}
                        <motion.main
                            key={tab}
                            className="page"
                            initial={
                                pageHidden
                                    ? false
                                    : reduced ? { opacity: 0 } : { opacity: 0, x: dir * 22 }
                            }
                            animate={
                                pageHidden
                                    ? { opacity: 0, x: 0, y: 20, scale: 0.985 }
                                    : { opacity: 1, x: 0, y: 0, scale: 1 }
                            }
                            transition={
                                reduced
                                    ? { duration: 0.2 }
                                    : pageArrived
                                      ? T.base
                                      : { ...T.slow, delay: 0.1 }
                            }
                            onAnimationComplete={() => { if (!pageHidden) setPageArrived(true) }}
                        >
                                {tab === "akey" && (
                                    <Akey {...{ loading, hidden, setHidden, cur, setCur, money, setDetail, go, say, highlight, txs, topup, balanceUsd, vitCount, vitProgress, rate: RATE_DEPO, pendingDeposit, balanceKnown: liveBalance.usd !== null }} />
                                )}
                                {tab === "transfe" && (
                                    <Transfe
                                        say={say}
                                        userId={userId}
                                        getAccessToken={session.getAccessToken}
                                        rateDepo={RATE_DEPO}
                                        rateRetre={RATE_RETRE}
                                        vitCount={vitCount}
                                        user={currentUser}
                                        onSubmitted={(tx: Tx) => {
                                            setTxs((prev) => [tx, ...prev])
                                            setProcessing(tx)
                                            refreshBalanceAndHistory()
                                        }}
                                    />
                                )}
                                {tab === "kat" && <Kat say={say} user={currentUser} getAccessToken={session.getAccessToken} />}
                                {tab === "notif" && (
                                    <Notifikasyon txs={txs} balanceUsd={balanceUsd} onOpenTx={(t: Tx) => setDetail(t)} />
                                )}
                                {/* Identical submit path to Transfè: prepend the
                                    row, then hand the SAME popup the same
                                    transaction. One component, two callers. */}
                                {tab === "rechaje" && (
                                    <Rechaje
                                        say={say}
                                        userId={userId}
                                        getAccessToken={session.getAccessToken}
                                        rateDepo={RATE_DEPO}
                                        vitCount={vitCount}
                                        user={currentUser}
                                        onSubmitted={(tx: Tx) => {
                                            setTopup((v) => v + tx.amount)
                                            setTxs((prev) => [tx, ...prev])
                                            setProcessing(tx)
                                            refreshBalanceAndHistory()
                                        }}
                                    />
                                )}
                                {tab === "istorik" && (
                                    <Istorik loading={loading} setDetail={setDetail} money={money} highlight={highlight} txs={txs} />
                                )}
                                {tab === "profil" && (
                                    <Profil say={say} txs={txs} topup={topup} user={currentUser} onSignOut={() => session.signOut()}
                                            balanceUsd={balanceUsd} vitCount={vitCount} rateDepo={RATE_DEPO} balanceKnown={liveBalance.usd !== null}
                                            depositsEnabled={liveRates?.deposits_enabled} transfersEnabled={liveRates?.transfers_enabled}
                                            avatarPref={avatarPref} setAvatarPref={setAvatarPref} getAccessToken={session.getAccessToken} />
                                )}
                        </motion.main>
                    </div>

                    {/* The dock's blur field. Rendered as the dock's own
                        sibling and pinned to the same edge, so the two move
                        together — the bar is never over unblurred ground.
                        Purely decorative: aria-hidden, no pointer events. */}
                    <div className="dock-veil" aria-hidden="true" />
                    <nav className="dock g g-sheet" aria-label="Navigasyon prensipal">
                        {TABS.map((t) => {
                            const isCenter = t.key === CENTER
                            const on = tab === t.key
                            return (
                                <Press
                                    key={t.key}
                                    className={`dock-item${on ? " on" : ""}${isCenter ? " dock-home" : ""}`}
                                    onClick={() => go(t.key)}
                                    scale={isCenter ? 0.92 : 0.9}
                                    /* No visible text, so the label has to live
                                       here or the bar is unusable with a screen
                                       reader. */
                                    aria-label={t.label}
                                    aria-current={on ? "page" : undefined}
                                >
                                    {on && !isCenter && (
                                        <motion.span layoutId="dock-pill" className="dock-pill" transition={SPRING} />
                                    )}
                                    <span className="dock-in">
                                        <Icon name={t.icon} size={isCenter ? 24 : 21} stroke={on ? 2.1 : 1.6} />
                                    </span>
                                </Press>
                            )
                        })}
                    </nav>
                </div>
            </div>

            <DraftPrompt draft={draftChecked ? draft : null}
                onResume={() => { go("transfe"); setDraft(null) }}
                onDiscard={() => { clearDraft(userId); setDraft(null) }} />
            <ProcessSheet tx={processing} onFollow={followTx} />
            <TxSheet tx={detail} onClose={() => setDetail(null)} money={money} say={say} user={currentUser} />
            <Toast msg={toast} />
            </>
            )}
        </div>
    )
}

// ═══════════════════════════════════════════════════════════════════════════
// AKÈY
// ═══════════════════════════════════════════════════════════════════════════
function Akey({ loading, hidden, setHidden, cur, setCur, money, setDetail, go, say, highlight, txs, topup, balanceUsd, vitCount, vitProgress, rate: RATE_DEPO, pendingDeposit, balanceKnown }: any) {
    const reduced = useReduced()
    // `txs` is always a real array (never undefined) once logged in — no
    // fixture fallback here, so a real account can never show fixture rows.
    const recent = (txs as Tx[]).slice(0, 4)
    return (
        <>
            {/* Hero. The only dark surface on the page, so the balance is
                unmistakably the foreground without needing to be enormous. */}
            <motion.section
                className="hero g g-dark"
                initial={reduced ? false : { opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={T.slow}
            >
                <div className="hero-top">
                    <span className="lbl on-dark">Balans Payvit</span>
                    <div className="hero-ctl">
                        <div className="seg g g-chip">
                            {(["USD", "HTG"] as const).map((k) => (
                                <Press key={k} className={`seg-b${cur === k ? " on" : ""}`} onClick={() => setCur(k)} scale={0.94}>
                                    {cur === k && <motion.span layoutId="cur-pill" className="seg-fill" transition={SPRING} />}
                                    <span>{k}</span>
                                </Press>
                            ))}
                        </div>
                        <Press className="ico-d" onClick={() => setHidden((v: boolean) => !v)}
                               aria-label={hidden ? "Montre balans" : "Kache balans"}>
                            <Icon name={hidden ? "eyeOff" : "eye"} size={18} color={C.onDark} />
                        </Press>
                    </div>
                </div>

                <div className="hero-num">
                    {loading ? (
                        <Skeleton h={44} w="62%" r={R.sm} style={{ marginTop: 6 }} />
                    ) : !balanceKnown ? (
                        // Real fetch failed rather than never having run —
                        // showing a fixture number here would misrepresent
                        // this specific account's own money as something it
                        // isn't. Honest unknown beats a confident wrong number.
                        <span className="pv-tc-zero" title="Nou pa t kapab chaje balans ou. Eseye ankò.">—</span>
                    ) : hidden ? (
                        "••••••"
                    ) : cur === "USD" ? (
                        <CountUp value={balanceUsd + (topup ?? 0)} format={(n) => fmtUSD(n)} />
                    ) : (
                        <CountUp value={(balanceUsd + (topup ?? 0)) * RATE_DEPO} format={(n) => fmtHTG(n)} />
                    )}
                </div>
                <div className="hero-sub">
                    {!loading && !balanceKnown ? "Nou pa t kapab chaje balans ou. Eseye ankò." : (
                        <>
                            {cur === "USD" ? `≈ ${fmtHTG(balanceUsd * RATE_DEPO)}` : `≈ ${fmtUSD(balanceUsd)}`}
                            {" · "}1 USD = {RATE_DEPO} HTG
                        </>
                    )}
                </div>

                {/* Icon only. The label repeated what the glyph already said;
                    the aria-label keeps it for anyone who cannot see it. */}
                <div className="hero-acts">
                    {/* Rechaje keeps its own labelled row below — a fourth
                        circle for it would say the same thing twice. */}
                    <Press className="act" onClick={() => go("transfe")} aria-label="Depoze lajan">
                        <Icon name="arrowDown" size={22} color="#fff" stroke={2.3} />
                    </Press>
                    <Press className="act" onClick={() => go("transfe")} aria-label="Voye lajan">
                        <Icon name="arrowUp" size={22} color="#fff" stroke={2.3} />
                    </Press>
                    <Press className="act" onClick={() => go("kat")} aria-label="Kat vityèl la">
                        <Icon name="card" size={22} color="#fff" stroke={2.3} />
                    </Press>
                </div>

                {/* 7 — Rechaje Balans, directly under the balance card. */}
                <Press className="topup" onClick={() => go("rechaje")}>
                    <span className="topup-ico raised"><Icon name="plus" size={18} color="#fff" stroke={2.4} /></span>
                    <span className="topup-lbl">Rechaje Balans</span>
                    <Icon name="chevronRight" size={16} color={C.onDarkMute} />
                </Press>
            </motion.section>

            {/* Pending deposit — the one thing worth surfacing before the
                user goes looking for it: a deposit sitting en_attente (or
                bounced back annulé) that they submitted but hasn't been
                confirmed by an admin yet. */}
            {pendingDeposit && (
                <div className="blocked" style={{ cursor: "pointer" }} onClick={() => go("istorik")}>
                    <Icon name="hourglass" size={16} color={C.warn} />
                    <span>
                        {pendingDeposit.statut === "annulé"
                            ? `Depo ${fmtUSD(pendingDeposit.montant)} ou an (${pendingDeposit.methode}) te anile.`
                            : `Depo ${fmtUSD(pendingDeposit.montant)} ou an (${pendingDeposit.methode}) ap tann konfimasyon.`}
                        {pendingDeposit.admin_note ? ` — ${pendingDeposit.admin_note}` : ""}
                    </span>
                </div>
            )}

            {/* Stats. Chip tier — they ride on the page, not over it. */}
            <section className="stats">
                {[
                    { k: "tx", label: "Tranzaksyon", value: String((txs as Tx[]).length), icon: "swap" as IconName },
                    { k: "vit", label: "VIT", value: String(vitCount), icon: "zap" as IconName },
                    { k: "tot", label: "Total", value: money(balanceUsd + (topup ?? 0)), icon: "wallet" as IconName },
                ].map((s, i) => (
                    <motion.div
                        key={s.k}
                        className="stat g g-card"
                        initial={reduced ? false : { opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ ...T.base, delay: reduced ? 0 : 0.05 + i * 0.05 }}
                    >
                        <span className="stat-ico"><Icon name={s.icon} size={16} color={C.teal} /></span>
                        <span className="lbl">{s.label}</span>
                        <div className="stat-v">{loading ? <Skeleton h={22} w="70%" /> : s.value}</div>
                        {s.k === "vit" && (
                            <div className="vit-bar">
                                {[0, 1, 2].map((n) => (
                                    <motion.span
                                        key={n}
                                        className="vit-seg"
                                        initial={reduced ? false : { scaleX: 0 }}
                                        animate={{ scaleX: 1 }}
                                        transition={{ ...T.base, delay: reduced ? 0 : 0.3 + n * 0.07 }}
                                        style={{ background: n < vitProgress ? C.teal : "rgba(26,157,139,.16)" }}
                                    />
                                ))}
                            </div>
                        )}
                    </motion.div>
                ))}
            </section>

            <section className="sec">
                <div className="sec-hd">
                    <h2 className="sec-t">Dènye tranzaksyon</h2>
                    <Press className="link" onClick={() => go("istorik")}>
                        Wè tout <Icon name="chevronRight" size={14} />
                    </Press>
                </div>
                <div className="list g g-card">
                    {loading ? (
                        [0, 1, 2, 3].map((i) => <RowSkeleton key={i} />)
                    ) : recent.length ? (
                        recent.map((t, i) => (
                            <Row key={t.id} tx={t} i={i} money={money} onOpen={() => setDetail(t)} hi={highlight === t.id} />
                        ))
                    ) : (
                        <div className="empty">
                            <Icon name="clock" size={26} color={C.textMute} />
                            <div>Ou pa gen tranzaksyon poko</div>
                        </div>
                    )}
                </div>
            </section>
        </>
    )
}

/** A transaction row. The avatar carries a `layoutId` so it flies into the
 *  detail sheet rather than the sheet appearing from nowhere — the single
 *  gesture that most makes a web app feel native. */
function Row({ tx, i, money, onOpen, hi }: { tx: Tx; i: number; money: (n: number) => string; onOpen: () => void; hi?: boolean }) {
    const reduced = useReduced()
    const st = STATUS[tx.statut]
    const out = tx.kind === "retre" || tx.kind === "transfe"
    return (
        <motion.div
            initial={reduced ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...T.base, delay: reduced ? 0 : i * 0.045 }}
        >
            <Press as="div" className={`row${hi ? " hi" : ""}`} data-tx={tx.id} onClick={onOpen} scale={0.985} role="button" tabIndex={0}>
                <motion.span layoutId={`av-${tx.id}`} transition={SPRING} className="row-av">
                    <Avatar seed={tx.seed} name={tx.name} size={40} />
                </motion.span>
                <div className="row-mid">
                    <div className="row-nm">{tx.name}</div>
                    <div className="row-sub">{tx.method} · {tx.at}</div>
                </div>
                <div className="row-end">
                    <div className={`row-amt${out ? " out" : ""}`}>
                        {out ? "−" : "+"}{money(tx.amount)}
                    </div>
                    <span className={`pill pill-${st.tone}`}>{st.label}</span>
                </div>
            </Press>
        </motion.div>
    )
}

function RowSkeleton() {
    return (
        <div className="row">
            <Skeleton w={40} h={40} r={R.pill} />
            <div className="row-mid" style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                <Skeleton h={13} w="52%" /><Skeleton h={10} w="34%" />
            </div>
            <div className="row-end" style={{ alignItems: "flex-end", gap: 7 }}>
                <Skeleton h={14} w={64} /><Skeleton h={16} w={52} r={R.pill} />
            </div>
        </div>
    )
}

// ═══════════════════════════════════════════════════════════════════════════
// TRANSFÈ — three steps, same machine as the live flow
// ═══════════════════════════════════════════════════════════════════════════
const STEPS = ["Detay", "Verifye", "Konfime"] as const
// ── Providers — PORTED VERBATIM from framer/TransfeFlow.tsx ─────────────────
// Same lists, same hues, same marks, same picker. The converter is a pattern
// users already know from the dashboard, so this is a copy, not a lookalike.
type Rail = "MonCash" | "NatCash" | "Payvit"
type Intl = "Cashapp" | "Meru" | "Wise" | "Zelle" | "Paypal"
type Provider = Rail | Intl

const RAILS: Rail[] = ["MonCash", "NatCash", "Payvit"]
/** Paypal is withdrawal-only: Payvit cannot take a deposit through it, so it
 *  is absent from the option list in Depo rather than present-and-disabled.
 *  An option a user can see but never use is worse than one that isn't there. */
const INTLS_ALL: Intl[] = ["Cashapp", "Meru", "Wise", "Zelle", "Paypal"]
const INTLS_DEPO: Intl[] = ["Cashapp", "Meru", "Wise", "Zelle"]
const intlsFor = (isDepo: boolean): Intl[] => (isDepo ? INTLS_DEPO : INTLS_ALL)
const INTLS: Intl[] = INTLS_ALL
/** Bridge Lokal moves gourdes between the two carrier wallets. The Payvit
 *  balance is not a bridge endpoint — that route is Rechaje. */
const BRIDGE_RAILS: Rail[] = ["MonCash", "NatCash"]

/** Brand hue per provider, adjusted where needed to hold contrast on glass. */
const BRAND_HUE: Record<Provider, string> = {
    MonCash: "#C42F27", // Digicel red, darkened
    NatCash: "#0E6F80", // Natcom teal
    Payvit: "#12897A", // our own — deep end of the brand gradient
    Cashapp: "#00A83E", // Cash App green, darkened
    Meru: "#5B4BD6",
    Wise: "#3F8C22", // Wise green, darkened hard — #9FE870 is unreadable here
    Zelle: "#6D1ED4",
    Paypal: "#003087", // PayPal navy
}

function BrandMark({ provider, size = 24 }: { provider: Provider; size?: number }) {
    const p = {
        width: size, height: size, viewBox: "0 0 24 24", fill: "none",
        stroke: BRAND_HUE[provider], strokeWidth: 1.6,
        strokeLinecap: "round" as const, strokeLinejoin: "round" as const,
        "aria-hidden": true, focusable: false,
    }
    switch (provider) {
        case "Cashapp": return (
            <svg {...p}><rect x="3" y="3" width="18" height="18" rx="5.5" /><path d="M12 7v10" />
                <path d="M14.6 10.1a2.5 2.5 0 0 0-2.4-1.5h-.4a1.95 1.95 0 0 0-.35 3.87l1.7.3a1.95 1.95 0 0 1-.35 3.87h-.4a2.5 2.5 0 0 1-2.4-1.5" /></svg>)
        case "Zelle": return (
            <svg {...p}><rect x="3" y="3" width="18" height="18" rx="5.5" /><path d="M12 5.6v2.2" />
                <path d="M12 16.2v2.2" /><path d="M8.9 7.8h6.2l-6.2 8.4h6.2" /></svg>)
        case "Wise": return (
            <svg {...p}><path d="m4 4.8 6.4 7.2L4 19.2" /><path d="m12.4 4.8 6.4 7.2-6.4 7.2" /></svg>)
        case "Paypal": return (
            <svg {...p}><path d="M6.6 20.4 8.9 4.2h6a3.9 3.9 0 0 1 0 7.8H9.7" />
                <path d="M10.2 20.4h3.2l.5-3.4h2.4a3.6 3.6 0 0 0 0-7.2h-.8" /></svg>)
        case "Payvit": return (
            // Traced from the Payvit logo onto the same 24px grid as the rest.
            <svg {...p}><path d="M2.4 4.2 17.8 3.9 21.4 9.8 14.3 20.4 6.8 20.4 2.4 14.7 10.2 9.4Z" />
                <circle cx="16.3" cy="7.2" r="1.35" /></svg>)
        case "Meru": return (
            <svg {...p}><path d="M2.5 19.2h19" />
                <path d="m5.6 19.2 5.2-9.6a1.35 1.35 0 0 1 2.4 0l5.2 9.6" />
                <path d="m9.5 13.4 1.4 1.15a1.15 1.15 0 0 0 1.45 0l1.3-1.05" /></svg>)
        case "MonCash": return (
            <svg {...p}><rect x="5.8" y="2.4" width="12.4" height="19.2" rx="3.2" /><path d="M10.4 5.3h3.2" />
                <circle cx="12" cy="13" r="3.3" /><path d="M12 11.1v3.8" /></svg>)
        default: return ( // NatCash
            <svg {...p}><rect x="5.8" y="2.4" width="12.4" height="19.2" rx="3.2" /><path d="M10.4 5.3h3.2" />
                <path d="M9.3 13.9a3.8 3.8 0 0 1 5.4 0" /><path d="M10.9 16.3a1.6 1.6 0 0 1 2.2 0" />
                <path d="M12 18.5h.01" /></svg>)
    }
}

/** Provider picker — the segmented sliding pill used throughout the app,
 *  widened to carry a brand mark beside each name. */
function ProviderPicker<T extends Provider>({
    options, value, onChange, layoutId,
}: { options: T[]; value: T; onChange: (v: T) => void; layoutId: string }) {
    return (
        <div className="pv-tc-picker">
            {options.map((opt) => {
                const active = opt === value
                return (
                    <button key={opt} type="button" className="pv-tc-pickopt"
                            onClick={() => onChange(opt)} aria-pressed={active} aria-label={opt}>
                        {active && (
                            <motion.div layoutId={layoutId} transition={SPRING} className="pv-tc-pickfill" />
                        )}
                        <span className="pv-tc-pickinner">
                            <span className="pv-tc-pickmark" data-active={active}>
                                <BrandMark provider={opt} size={24} />
                            </span>
                            <span className="pv-tc-picklabel">{opt}</span>
                        </span>
                    </button>
                )
            })}
        </div>
    )
}

/** The Detay → Verifye → Konfime marker. One component, so Rechaje shows the
 *  same three steps as every other route rather than a lookalike of them. */
function StepRail({ step }: { step: number }) {
    return (
        <div className="rail-steps">
            {STEPS.map((s, i) => (
                <div key={s} className={`stp${i === step ? " on" : ""}${i < step ? " done" : ""}`}>
                    <span className="stp-n">
                        {i === step && <motion.span layoutId="stp-pill" className="stp-fill" transition={SPRING} />}
                        <span className="stp-num">{i < step ? <Icon name="check" size={13} stroke={2.6} /> : i + 1}</span>
                    </span>
                    <span className="stp-l">{s}</span>
                    {i < STEPS.length - 1 && <span className="stp-bar" />}
                </div>
            ))}
        </div>
    )
}

// ═══════════════════════════════════════════════════════════════════════════
// VERIFICATION — step 2, and ONLY step 2
//
// One hook and one box, shared by every transaction type including Rechaje.
// The point of building it once is that "the gate" cannot drift between
// routes: there is a single definition of what counts as verified, and every
// flow that gates on it gates identically.
// ═══════════════════════════════════════════════════════════════════════════

/** Where the user actually sends the money. Keyed by the PAY-side provider, so
 *  the box always names the account the funds are leaving for. */
const RAIL_ACCOUNT: Record<string, string> = {
    MonCash: "509 39 24 2032",
    NatCash: "509 35 08 8962",
    Payvit: "Balans Payvit ou",
}
const WALLET_HANDLE: Record<string, string> = {
    Cashapp: "$Payvitht", Meru: "$romeo10", Wise: "@medjysonr",
    Zelle: "+1 (786) 344-0163", Paypal: "@Medjyson",
}

/** What a saved recipient needs depends entirely on which kind of account is
 *  RECEIVING: a rail (MonCash/NatCash) needs a phone number, an
 *  international wallet needs that wallet's own tag/handle format — never
 *  both, and never a generic "Cashtag" label regardless of the real
 *  provider. */
const TAG_LABEL: Record<Intl, string> = {
    Cashapp: "Cashtag", Meru: "Meru tag", Wise: "Wise tag",
    Zelle: "Nimewo oswa imèl Zelle", Paypal: "Non itilizatè PayPal",
}
const TAG_PLACEHOLDER: Record<Intl, string> = {
    Cashapp: "$non-itilizatè", Meru: "$non-itilizatè", Wise: "@non-itilizatè",
    Zelle: "+509 38 12 3456 oswa imèl", Paypal: "@non-itilizatè",
}

/** Saved recipients — a user-created address book, not fixture data. Keyed
 *  per-user, same localStorage convention as the transfer draft (and the
 *  same key shape the real TransfeFlow.tsx uses for its own recipient
 *  store), so a recipient saved once is there on the next visit. */
interface SavedRecipient {
    id: string
    seed: string
    provider: Provider
    label: string
    handle: string
}
const recipientKey = (userId: string) => `payvit-recipients:${userId || "anon"}`
function loadRecipients(userId: string): SavedRecipient[] {
    try {
        const raw = localStorage.getItem(recipientKey(userId))
        return raw ? (JSON.parse(raw) as SavedRecipient[]) : []
    } catch (_e) {
        return []
    }
}
function saveRecipients(userId: string, list: SavedRecipient[]) {
    try { localStorage.setItem(recipientKey(userId), JSON.stringify(list)) } catch (_e) {}
}

/** PLACEHOLDER QR — generated once into a data URI so nothing is fetched at
 *  runtime. Swap this for the real payment QR and every box picks it up. */
const RAIL_QR = (() => {
    const N = 25
    const finder = (x: number, y: number, ox: number, oy: number) =>
        x >= ox && x < ox + 7 && y >= oy && y < oy + 7 &&
        (x === ox || x === ox + 6 || y === oy || y === oy + 6 ||
            (x >= ox + 2 && x <= ox + 4 && y >= oy + 2 && y <= oy + 4))
    let cells = ""
    for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
        const inFinder = finder(x, y, 0, 0) || finder(x, y, N - 7, 0) || finder(x, y, 0, N - 7)
        const reserved = (x < 8 && y < 8) || (x > N - 9 && y < 8) || (x < 8 && y > N - 9)
        const on = inFinder || (!reserved && ((x * 7 + y * 13) ^ (x * y)) % 3 === 0)
        if (on) cells += `<rect x="${x}" y="${y}" width="1" height="1"/>`
    }
    return "data:image/svg+xml;utf8," + encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${N} ${N}" shape-rendering="crispEdges">` +
        `<rect width="${N}" height="${N}" fill="#fff"/><g fill="#0B2E2C">${cells}</g></svg>`)
})()

const PROOF_MAX_BYTES = 8 * 1024 * 1024
const PROOF_ACCEPT = "image/png,image/jpeg,image/webp,image/heic,application/pdf"

/** `dataUrl` is what actually ships to the backend as `proof_image` — read
 *  eagerly here (not deferred to submit time) so submit never has to touch
 *  the File object again, matching the real TransfeFlow.tsx's inline
 *  base64-on-pick pattern. This was previously missing entirely (only
 *  name/size/isPdf were kept), which meant proof upload could never
 *  actually send bytes anywhere. */
type Proof = { name: string; size: number; isPdf: boolean; dataUrl: string }

/** The single definition of "verified".
 *
 *  Which evidence is demanded follows the account the money LEAVES FROM, and
 *  it is one or the other, never both:
 *
 *    MonCash / NatCash → a 12-digit TXN. The operator SMSes one; that number
 *                        is what we reconcile the payment against.
 *    Cashapp / Wise / Meru / Zelle / Paypal → an uploaded proof. These issue
 *                        no 12-digit reference at all, so demanding one would
 *                        ask for a number that does not exist.
 *    Balans Payvit     → neither. The money never left Payvit, so there is
 *                        nothing for the user to evidence.
 *
 *  `missing` is the list the UI shows; empty means the gate is open. Returning
 *  the reasons rather than a bare boolean is what lets the button explain
 *  itself instead of just going grey. */
function useVerification(source: Provider) {
    const [txn, setTxn] = React.useState("")
    const [txnTouched, setTxnTouched] = React.useState(false)
    const [proof, setProof] = React.useState<Proof | null>(null)
    const [proofError, setProofError] = React.useState<string | null>(null)
    const [dragOver, setDragOver] = React.useState(false)

    const txnValid = /^\d{12}$/.test(txn)

    const onProofPick = (file: File | null | undefined) => {
        setProofError(null)
        if (!file) return
        const okType = file.type.startsWith("image/") || file.type === "application/pdf"
        if (!okType) {
            setProofError("Fòma sa a pa aksepte. Voye yon foto (PNG, JPG, WEBP) oswa yon PDF.")
            return
        }
        if (file.size > PROOF_MAX_BYTES) {
            setProofError(`Fichye a twò gwo (${(file.size / 1048576).toFixed(1)} Mo). Maksimòm se 8 Mo.`)
            return
        }
        const isPdf = file.type === "application/pdf"
        const reader = new FileReader()
        reader.onload = () => {
            setProof({ name: file.name, size: file.size, isPdf, dataUrl: String(reader.result) })
        }
        reader.onerror = () => setProofError("Nou pa t kapab li fichye a. Eseye ankò.")
        reader.readAsDataURL(file)
    }

    /** The screenshot is the evidence that always counts — it is the only
     *  thing that exists for every payment path, so it is what the gate turns
     *  on. The TXN number is asked for ON TOP of it where the operator issues
     *  one (MonCash, NatCash), because it speeds reconciliation, but it is
     *  optional: a user who cannot find the SMS must still be able to finish.
     *  A HALF-typed number is not optional though — that is a mistake, not an
     *  abstention, so it blocks until completed or cleared. */
    const showsTxn = source === "MonCash" || source === "NatCash"
    const needsProof = source !== "Payvit"
    const txnPartial = showsTxn && txn.length > 0 && !txnValid

    const missing: string[] = []
    if (needsProof && !proof) missing.push("yon prèv tranzaksyon")
    if (txnPartial) missing.push("nimewo TXN konplè a (12 chif) — oswa efase l")

    const reset = () => {
        setTxn(""); setTxnTouched(false); setProof(null); setProofError(null); setDragOver(false)
    }

    return { txn, setTxn, txnTouched, setTxnTouched, txnValid, proof, setProof,
             proofError, onProofPick, dragOver, setDragOver,
             showsTxn, needsProof, txnPartial,
             /** True only when a complete number was actually supplied. */
             hasTxn: showsTxn && txnValid,
             ok: missing.length === 0, missing, reset }
}

type Verification = ReturnType<typeof useVerification>

/** VIT has two ways to be spent, and the difference is where the value lands.
 *
 *  PORTED VERBATIM from framer/TransfeFlow.tsx:1890-1916 — same whole-token
 *  rule, same cap, same "add leaves the fee alone" behaviour. Anything that
 *  differed here would be a bug against the live app, not a decision.
 *
 *    reduce (−)  spend only as many whole tokens as fit inside the fee, and
 *                take them off the fee. Never pays out beyond the fee.
 *    add (+)     spend the WHOLE balance, and hand its value to the recipient.
 *                The fee is untouched.
 *
 *  Whole tokens throughout: VIT is earned in ones, so a half-token spend is a
 *  quantity the dashboard could never display or subtract. */
type VitMode = "reduce" | "add"
function applyVit(on: boolean, mode: VitMode, feeUsd: number, vitCount: number) {
    const spend = !on
        ? 0
        : mode === "reduce"
          ? Math.min(vitCount, Math.max(1, Math.floor(feeUsd / VIT_USD_RATE)))
          : vitCount
    const spendUsd = spend * VIT_USD_RATE
    const appliedUsd = mode === "reduce" ? Math.min(spendUsd, feeUsd) : spendUsd
    return {
        spend,
        appliedUsd,
        feeAfterUsd: mode === "reduce" ? Math.max(0, feeUsd - appliedUsd) : feeUsd,
        boostUsd: mode === "add" ? spendUsd : 0,
    }
}

/** Step 2's brief, above the payment box.
 *
 *  Step 2 is where the user is about to go and actually move money, so it is
 *  the last moment the amount can still be acted on — which makes it the right
 *  place for the VIT decision, and the wrong place to make them scroll back to
 *  Detay to remember what they owe. Step 3 shows the same figures with nothing
 *  left to change.
 *
 *  Fees are quoted GROSS with the VIT discount as its own line, so the rows
 *  add up to the total instead of a net fee appearing alongside a rebate that
 *  has already been taken out of it. */
function PayBrief({
    amount, feeGross, feeNet, cur, dp, vitOn, setVitOn, vitSpend,
    vitMode, setVitMode, boost, boostCur, boostDp, vitCount,
}: {
    amount: number; feeGross: number; feeNet: number; cur: string; dp: number
    vitOn: boolean; setVitOn: (v: boolean) => void; vitSpend: number
    vitMode: VitMode; setVitMode: (m: VitMode) => void
    boost: number; boostCur: string; boostDp: number; vitCount: number
}) {
    const discount = feeGross - feeNet
    const total = amount + feeNet
    return (
        <div className="pb g g-card">
            <span className="lbl">Sa pou w voye</span>
            <div className="pb-big n">{fmt(total, dp)}<span> {cur}</span></div>
            <div className="pb-rows">
                <div className="pb-row"><span>Montan</span>
                    <span className="n">{fmt(amount, dp)} {cur}</span></div>
                <div className="pb-row"><span>Frè Payvit</span>
                    <span className="n">{fmt(feeGross, dp)} {cur}</span></div>
                <AnimatePresence initial={false}>
                    {vitOn && vitSpend > 0 && (
                        <motion.div className="pb-row ok" key={vitMode}
                            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }} transition={T.base} style={{ overflow: "hidden" }}>
                            <span>
                                {vitMode === "reduce" ? "Diminye frè a" : "Ogmante sa yo resevwa"}
                                {" · "}{vitSpend} VIT
                            </span>
                            <span className="n">
                                {vitMode === "reduce"
                                    ? `−${fmt(discount, dp)} ${cur}`
                                    : `+${fmt(boost, boostDp)} ${boostCur}`}
                            </span>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
            <div className="pb-vit">
                <span className="pb-vitk">
                    <Icon name="zap" size={16} color={C.teal} /> Aplike VIT
                    <span className="vit-chip">{vitCount} VIT</span>
                </span>
                <span className="pb-vitctl">
                    {/* − / + — what the VIT is SPENT ON, not whether it is
                        spent. Only meaningful once VIT is on, so it appears
                        beside the Wi/Non rather than replacing it. */}
                    <AnimatePresence initial={false}>
                        {vitOn && vitCount >= 1 && (
                            <motion.span className="seg seg-sm g g-chip"
                                initial={{ opacity: 0, width: 0, marginRight: -S[2] }}
                                animate={{ opacity: 1, width: "auto", marginRight: 0 }}
                                exit={{ opacity: 0, width: 0, marginRight: -S[2] }}
                                transition={T.quick}>
                                {(["reduce", "add"] as VitMode[]).map((m) => (
                                    <Press key={m} className={`seg-b${vitMode === m ? " on" : ""}`}
                                           onClick={() => setVitMode(m)} scale={0.94}
                                           aria-pressed={vitMode === m}
                                           aria-label={m === "reduce" ? "Diminye frè a" : "Ogmante kantite a"}>
                                        {vitMode === m && (
                                            <motion.span layoutId="vit-mode" className="seg-fill" transition={SPRING} />
                                        )}
                                        <span style={{ display: "flex" }}>
                                            <Icon name={m === "reduce" ? "minus" : "plus"} size={15}
                                                  color={vitMode === m ? "#fff" : C.textSub} stroke={2.6} />
                                        </span>
                                    </Press>
                                ))}
                            </motion.span>
                        )}
                    </AnimatePresence>
                    <span className="seg g g-chip">
                        {[false, true].map((v) => (
                            <Press key={String(v)} className={`seg-b${vitOn === v ? " on" : ""}`}
                                   onClick={() => setVitOn(v)} scale={0.94} disabled={vitCount < 1}>
                                {vitOn === v && <motion.span layoutId="vit-pill" className="seg-fill" transition={SPRING} />}
                                <span>{v ? "Wi" : "Non"}</span>
                            </Press>
                        ))}
                    </span>
                </span>
            </div>
            {vitOn && vitCount >= 1 && (
                <p className="hint">
                    {vitMode === "reduce"
                        ? "VIT la ap peye yon pati nan frè a."
                        : "VIT la ap ajoute sou sa moun nan ap resevwa — frè a pa chanje."}
                </p>
            )}
            {vitCount < 1 && (
                <p className="hint">Ou pa gen VIT disponib pou kounye a.</p>
            )}
        </div>
    )
}

/** The step-2 box. Which header it wears depends on where the money is coming
 *  FROM — a Haitian rail gets the QR and the account number to pay, an
 *  international wallet gets the handle. Both then ask for the same two
 *  pieces of evidence. */
function VerifyBox({ source, v }: { source: Provider; v: Verification }) {
    const isRail = source === "MonCash" || source === "NatCash"
    const fileRef = React.useRef<HTMLInputElement | null>(null)
    return (
        <section className="sec">
            <h2 className="sec-t">Konfime peman {source} ou</h2>
            <div className="vfy g g-card">
                {isRail ? (
                    <div className="vfy-top">
                        <span className="vfy-qr"><img src={RAIL_QR} alt="" width={104} height={104} /></span>
                        <div className="vfy-acct">
                            <span className="lbl">Voye sou {source}</span>
                            <div className="vfy-num n">{RAIL_ACCOUNT[source] ?? "—"}</div>
                        </div>
                    </div>
                ) : (
                    <div className="vfy-handle">
                        <span className="vfy-mark"><BrandMark provider={source} size={22} /></span>
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <span className="lbl">Voye sou {source}</span>
                            <div className="vfy-num n">
                                {WALLET_HANDLE[source] ?? RAIL_ACCOUNT[source] ?? "—"}
                            </div>
                        </div>
                    </div>
                )}

                {/* TXN — rails only, and optional. MonCash and NatCash SMS a
                    12-digit reference; nothing else does, so nothing else is
                    asked for one. */}
                {v.showsTxn && (
                    <>
                        <span className="vfy-lbl">
                            Nimewo TXN <span className="vfy-opt">opsyonèl</span>
                        </span>
                        <label className="field g g-chip vfy-txn">
                            <Icon name="shield" size={18} color={C.textMute} />
                            <input
                                value={v.txn}
                                onChange={(e) => v.setTxn(e.target.value.replace(/\D/g, "").slice(0, 12))}
                                onBlur={() => v.setTxnTouched(true)}
                                inputMode="numeric" placeholder="123456789012"
                                aria-label="Nimewo TXN" aria-invalid={v.txnTouched && !v.txnValid}
                                style={{ letterSpacing: ".06em" }}
                            />
                            <span className="vfy-count n" data-ok={v.txnValid}>{v.txn.length}/12</span>
                        </label>
                        {v.txnTouched && v.txn.length > 0 && !v.txnValid && (
                            <div className="vfy-err">Nimewo TXN an dwe gen egzakteman 12 chif.</div>
                        )}
                    </>
                )}

                {/* Proof — required on every path that leaves Payvit. The
                    hidden input is the real control; the styled block is its
                    label, so click, keyboard and drop all land on one element. */}
                {v.needsProof && (
                    <>
                        <span className="vfy-lbl">
                            Prèv tranzaksyon <span className="vfy-star" aria-label="obligatwa">*</span>
                        </span>
                        <input ref={fileRef} type="file" accept={PROOF_ACCEPT} hidden
                               onChange={(e) => { v.onProofPick(e.target.files?.[0]); e.target.value = "" }} />
                        <div className="drop g g-chip vfy-drop" data-drag={v.dragOver} data-has={!!v.proof}
                             role="button" tabIndex={0}
                             onClick={() => fileRef.current?.click()}
                             onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fileRef.current?.click() } }}
                             onDragOver={(e) => { e.preventDefault(); v.setDragOver(true) }}
                             onDragLeave={() => v.setDragOver(false)}
                             onDrop={(e) => { e.preventDefault(); v.setDragOver(false); v.onProofPick(e.dataTransfer.files?.[0]) }}>
                            <Icon name={v.proof ? "check" : "receipt"} size={20}
                                  color={v.proof ? C.ok : C.teal} stroke={v.proof ? 2.6 : 1.6} />
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <div className="drop-t">{v.proof ? v.proof.name : "Mete yon foto oswa yon PDF"}</div>
                                <div className="drop-s">
                                    {v.proof
                                        ? `${Math.max(1, Math.round(v.proof.size / 1024))} Ko · tape pou chanje l`
                                        : "Prèv tranzaksyon an — maksimòm 8 Mo."}
                                </div>
                            </div>
                            {v.proof && (
                                <Press className="vfy-x" scale={0.9} aria-label="Retire prèv la"
                                       onClick={(e: any) => { e.stopPropagation(); v.setProof(null) }}>
                                    <Icon name="x" size={15} color={C.textMute} stroke={2.2} />
                                </Press>
                            )}
                        </div>
                        {v.proofError && <div className="vfy-err">{v.proofError}</div>}
                    </>
                )}

                <p className="hint">
                    {v.showsTxn
                        ? `Voye lajan an sou kont ki anwo a, epi mete foto konfimasyon an. ${source} voye yon SMS ak yon nimewo 12 chif tou — si w jwenn li, kole l anwo a pou nou verifye pi vit.`
                        : v.needsProof
                          ? `${source} pa bay nimewo TXN. Voye lajan an sou kont ki anwo a, epi mete yon foto oswa yon PDF konfimasyon an isit la.`
                          : "Lajan an pa janm kite Payvit, kidonk pa gen anyen pou verifye."}
                </p>
            </div>
        </section>
    )
}

// ═══════════════════════════════════════════════════════════════════════════
// STEP 3 · KONFIME — one summary, every transaction type
//
// The frame is fixed: avatar with its verified badge, title, subtitle, a card
// of icon rows, the reference number with its copy button, and the confirm
// CTA. ONLY the rows differ between types, because only the details differ —
// which is why `rows` is the single prop that varies and everything else is
// hard-coded here rather than passed in per caller.
// ═══════════════════════════════════════════════════════════════════════════
type SumRow =
    | { div: true }
    | { icon: IconName; k: string; v: React.ReactNode; tone?: "ok"; node?: never }
    | { icon: IconName; k: string; node: React.ReactNode; v?: never; tone?: never }

/** Reference numbers are per-type and must not change while the user is
 *  looking at them, so they are minted once when the summary is first built. */
const refFor = (prefix: string) =>
    `${prefix}-${Date.now().toString(36).toUpperCase().slice(-6)}-${Math.random().toString(36).toUpperCase().slice(2, 6)}`

function KonfimeStep({
    seed, name, rows, reference, onEdit, onConfirm, busy, say, blocked,
}: {
    seed: string; name: string; rows: SumRow[]; reference: string
    onEdit: () => void; onConfirm: () => void; busy: boolean
    say: (m: string) => void; blocked?: string[]
}) {
    const reduced = useReduced()
    return (
        <div className="kf">
            <div className="kf-hd">
                <motion.span className="kf-av"
                    initial={reduced ? false : { scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }} transition={SPRING}>
                    <Avatar seed={seed} name={name} size={104} />
                    <motion.span className="kf-badge"
                        initial={reduced ? false : { scale: 0 }}
                        animate={{ scale: 1 }} transition={{ ...SPRING, delay: reduced ? 0 : 0.14 }}>
                        <Icon name="check" size={19} color="#fff" stroke={3} />
                    </motion.span>
                </motion.span>
                <h2 className="kf-t">Rezime Tranzaksyon</h2>
                <p className="kf-p">Tcheke chak enfòmasyon anvan konfimasyon.</p>
            </div>

            <div className="kf-card g g-card">
                {rows.map((r, i) =>
                    "div" in r ? (
                        <div key={`d${i}`} className="kf-div" />
                    ) : (
                        <div key={r.k} className="kf-row">
                            <span className="kf-k">
                                <Icon name={r.icon} size={18} color={C.textMute} stroke={1.7} />
                                {r.k}
                            </span>
                            {"node" in r && r.node !== undefined ? (
                                <span className="kf-node">{r.node}</span>
                            ) : (
                                <span className="kf-v n" style={r.tone === "ok" ? { color: C.ok } : undefined}>
                                    {r.v}
                                </span>
                            )}
                        </div>
                    )
                )}
            </div>

            <div className="kf-ref g g-card">
                <div style={{ minWidth: 0 }}>
                    <span className="lbl">Nimewo Tranzaksyon</span>
                    <div className="kf-refnum n">{reference}</div>
                </div>
                <Press className="kf-copy" scale={0.95}
                       onClick={() => {
                           try { navigator.clipboard?.writeText(reference) } catch (_e) {}
                           say("Nimewo kopye")
                       }}>
                    <Icon name="copy" size={17} color={C.tealDeep} stroke={1.9} /> Kopye
                </Press>
            </div>
            <p className="kf-note">Konsève nimewo sa a — li ap sèvi pou verifye tranzaksyon w lan.</p>

            {blocked?.length ? <Blocked missing={blocked} /> : null}
            {/* The way back. With the corner pencil gone this is the only
                route to Detay, and it is the same ghost/primary pair steps 1
                and 2 already use rather than a control invented for step 3. */}
            <div className="btn-row">
                <Press className="cta cta-ghost" onClick={onEdit}>Retounen</Press>
                <Press className="cta" onClick={onConfirm} disabled={busy || !!blocked?.length}>
                    {busy
                        ? <><span className="spin" /> N ap voye…</>
                        : <>Konfime tranzaksyon an <Icon name="send" size={17} color="#fff" /></>}
                </Press>
            </div>
        </div>
    )
}

/** What is still missing, said out loud. A disabled button that does not
 *  explain itself is the failure mode this exists to prevent. */
function Blocked({ missing }: { missing: string[] }) {
    if (!missing.length) return null
    return (
        <div className="blocked">
            <Icon name="shield" size={16} color={C.warn} />
            <span>Pou kontinye, ou bezwen {missing.join(" ak ")}.</span>
        </div>
    )
}

function Transfe({ say, onSubmitted, userId, getAccessToken, rateDepo: RATE_DEPO, rateRetre: RATE_RETRE, vitCount, user: USER }: {
    say: (m: string) => void; onSubmitted: (t: Tx) => void
    userId: string; getAccessToken: () => Promise<string | null>
    rateDepo: number; rateRetre: number; vitCount: number
    user: { seed: string; name: string; email: string; phone: string | null }
}) {
    const reduced = useReduced()
    /** RESTORED — the routing screen that chooses Depo / Transfè / Bridge
     *  before the flow starts, ported from PayvitDashboard.tsx's transferPage.
     *  Same three options, same Kreyòl subtitles explaining what each one
     *  actually does, same badges. `null` means "not chosen yet", which is the
     *  routing screen; anything else enters the flow. */
    const [mode, setMode] = React.useState<TxRoute | null>(null)
    const [step, setStep] = React.useState(0)
    const [travel, setTravel] = React.useState(1)
    const [depo, setDepo] = React.useState(true)
    /** Which wallet each side is on. Two independent pickers, exactly as the
     *  live flow has them: a rail (MonCash / NatCash / Payvit) and an
     *  international wallet. Swapping direction swaps which side owns which. */
    const [rail, setRail] = React.useState<Rail>("MonCash")
    const [intl, setIntl] = React.useState<Intl>("Cashapp")
    /** Bridge has no rail/intl split — both ends are the same family, so it
     *  needs its own from/to pair per variant. Kept separate from rail/intl so
     *  switching routes never silently rewrites the other route's choice. */
    const [bLocFrom, setBLocFrom] = React.useState<Rail>("MonCash")
    const [bLocTo, setBLocTo] = React.useState<Rail>("NatCash")
    /** Bridge Lokal only: a screenshot of the RECIPIENT's own MonCash/NatCash
     *  QR code (whichever app `bLocTo` is), so Payvit can pay them directly
     *  on that rail. Distinct from `verify.proof`, which is evidence of the
     *  sender paying INTO Payvit's own rail account — two different
     *  photos, two different purposes. Cleared on a `bLocTo` switch since a
     *  QR screenshotted for one app is meaningless for the other. */
    const [bridgeQr, setBridgeQr] = React.useState<{ name: string; dataUrl: string } | null>(null)
    const [bridgeQrError, setBridgeQrError] = React.useState<string | null>(null)
    const bridgeQrFileRef = React.useRef<HTMLInputElement | null>(null)
    React.useEffect(() => { setBridgeQr(null); setBridgeQrError(null) }, [bLocTo])
    /** No live phone source exists anywhere (not the JWT, not the balance or
     *  history endpoints) — but Bridge Lokal's backend call requires a real
     *  `sender_phone` (min 8 chars), so it's captured directly here rather
     *  than pulled from a fixture that no longer exists. */
    const [myPhone, setMyPhone] = React.useState("")
    const [bIntFrom, setBIntFrom] = React.useState<Intl>("Cashapp")
    const [bIntTo, setBIntTo] = React.useState<Intl>("Wise")
    const [openPicker, setOpenPicker] = React.useState<"pay" | "receive" | null>(null)
    /** The bridge beneficiary. Was an uncontrolled input, so the summary had
     *  nothing to name — the address the money is going to has to survive to
     *  step 3 or the confirmation is confirming a blank. */
    const [benef, setBenef] = React.useState("")
    const [amt, setAmt] = React.useState("5000")
    /** Raw text of whatever the user is CURRENTLY typing into the receive
     *  box, in the receive currency — null means "not being edited right
     *  now, show the computed conversion instead." Editing the pay side (or
     *  changing route/provider) clears it back to null, so the receive box
     *  never gets stuck showing stale text once the thing it was computed
     *  against has changed. */
    const [recvOverride, setRecvOverride] = React.useState<string | null>(null)
    const [rid, setRid] = React.useState<string | null>(null)
    const [note, setNote] = React.useState("")
    const [sheet, setSheet] = React.useState(false)
    /** Real, user-created recipients — no fixture seed. The account's own
     *  backend record is the source of truth (so it follows a login to any
     *  browser/device); the localStorage copy is only a same-device cache
     *  shown immediately while that fetch is in flight, and a fallback if
     *  it fails outright, so a network blip doesn't look like an emptied
     *  address book. */
    const [recipients, setRecipients] = React.useState<SavedRecipient[]>(() => loadRecipients(userId))
    React.useEffect(() => {
        setRecipients(loadRecipients(userId))
        let live = true
        ;(async () => {
            const token = await getAccessToken()
            if (!token) return
            const remote = await fetchRecipients(token)
            if (!live || !remote) return
            const mapped: SavedRecipient[] = remote.map((r: RemoteRecipient) => ({
                id: r.id, seed: `rec-${r.id}`, provider: r.provider as Provider, label: r.label, handle: r.handle,
            }))
            setRecipients(mapped)
            saveRecipients(userId, mapped)
        })()
        return () => { live = false }
    }, [userId])
    const [busy, setBusy] = React.useState(false)
    const [vitOn, setVitOn] = React.useState(false)
    const [vitMode, setVitMode] = React.useState<VitMode>("reduce")

    /** ── Converter model ────────────────────────────────────────────────
     *  One model for all four routes, so Depo, Retrè, Bridge Lokal and Bridge
     *  Entènasyonal render through the SAME markup. What changes per route is
     *  only which provider list each side draws from and which currency each
     *  side is denominated in — never the layout.
     *
     *  Bridge is same-currency by definition (gourdes to gourdes, or dollars
     *  to dollars), so its "conversion" is the identity and its rate chip says
     *  so rather than quoting a number that would always be 1. */
    const isBridge = mode === "bridge" || mode === "bridge-intl"
    const isLocalBridge = mode === "bridge"
    const rate = depo ? RATE_DEPO : RATE_RETRE
    const typed = parseFloat(amt) || 0

    const payProvider: Provider = isBridge
        ? (isLocalBridge ? bLocFrom : bIntFrom)
        : depo ? rail : intl
    const receiveProvider: Provider = isBridge
        ? (isLocalBridge ? bLocTo : bIntTo)
        : depo ? intl : rail

    /** A stale receive-box override (typed against the OLD rate/currency)
     *  would otherwise keep displaying once the route/provider it was
     *  computed against no longer applies. */
    React.useEffect(() => { setRecvOverride(null) }, [mode, depo, rail, intl, bLocFrom, bLocTo, bIntFrom, bIntTo])

    /** Declared here, below payProvider, because which evidence the gate
     *  demands is derived from the account the money leaves FROM. */
    const verify = useVerification(payProvider)
    /** Evidence belongs to one payment from one account. Changing the account
     *  it came from invalidates it — otherwise a TXN typed for MonCash stays
     *  attached after switching to NatCash. */
    React.useEffect(() => { verify.reset() }, [payProvider])

    const payCur = isBridge ? (isLocalBridge ? "HTG" : "USD") : depo ? "HTG" : "USD"
    const receiveCur = isBridge ? payCur : depo ? "USD" : "HTG"
    const payDp = payCur === "USD" ? 2 : 0
    const receiveDp = receiveCur === "USD" ? 2 : 0
    const convertedBase = isBridge ? typed : depo ? typed / RATE_DEPO : typed * RATE_RETRE

    /** Fees are quoted in USD everywhere, so an HTG-denominated route has to
     *  cross the rate first — including Bridge Lokal, which is gourdes on both
     *  ends but still priced off the dollar tier table. */
    const usd = payCur === "USD" ? typed : typed / rate
    const fee = calcFee(usd)
    const vit = applyVit(vitOn, vitMode, fee, vitCount)
    const { spend: vitSpend, appliedUsd: vitSaveUsd, feeAfterUsd: feeAfter, boostUsd } = vit
    /** "Add" hands the VIT's value to the RECIPIENT, so it lands on the
     *  receive side in the receive currency — matching the live flow's
     *  `recipientGets = usdValue + boostUsd` / `(typed + boostUsd) * RATE`. */
    const boostInReceive = receiveCur === "USD" ? boostUsd : boostUsd * rate
    const converted = convertedBase + boostInReceive
    /** Scoped to the RECEIVE side's provider — a recipient saved for Meru
     *  isn't offered when the receive side is now MonCash. Falls out of `rid`
     *  naturally on a provider switch, no separate reset effect needed. */
    const recipientsForReceive = recipients.filter((r) => r.provider === receiveProvider)
    const recip = recipientsForReceive.find((r) => r.id === rid) ?? null
    const feeInPay = payCur === "USD" ? feeAfter : feeAfter * rate

    const goStep = (n: number) => { setTravel(n >= step ? 1 : -1); setStep(n) }

    // ═══════════════════════════════════════════════════════════════════
    // PAYVIT-TO-PAYVIT (mode === "interne") — deliberately NOT folded into
    // the provider/rate machinery above: there's no rail, no international
    // wallet, no conversion, and per InternalTransferService::execute() no
    // fee at all (the money never leaves Payvit, so there's nothing for a
    // fee to recover) and no admin approval step — it completes instantly,
    // synchronously, unlike every other route here which lands `en_attente`.
    // ═══════════════════════════════════════════════════════════════════
    const [interneEmail, setInterneEmail] = React.useState("")
    const [interneAmt, setInterneAmt] = React.useState("20")
    const [interneBusy, setInterneBusy] = React.useState(false)
    const interneTyped = parseFloat(interneAmt) || 0
    const interneEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(interneEmail)
    const interneReference = React.useMemo(() => refFor("VIT"), [mode])

    const interneSumRows: SumRow[] = [
        { icon: "user", k: "Resipyan", v: interneEmail.trim() || "—" },
        { icon: "download", k: "Ou voye", v: `${fmt(interneTyped, 2)} USD` },
        { icon: "wallet", k: "Frè Payvit", v: "Gratis", tone: "ok" },
        { div: true },
        { icon: "arrowDown", k: "Yo resevwa", v: `${fmt(interneTyped, 2)} USD`, tone: "ok" },
    ]

    const interneSubmit = async () => {
        setInterneBusy(true)
        const token = await getAccessToken()
        if (!token) {
            setInterneBusy(false)
            say("Sesyon ekspire — konekte ankò.")
            return
        }
        const result = await submitInterne(
            { receiver_email: interneEmail.trim(), montant_usd: interneTyped },
            token
        )
        setInterneBusy(false)
        if (!result.ok) {
            say(result.error ?? "Erè. Eseye ankò.")
            return
        }
        onSubmitted({
            id: `vit-${Date.now().toString(36)}`, seed: `p2p-${interneEmail.trim()}`, kind: "transfe",
            name: interneEmail.trim().split("@")[0], handle: interneEmail.trim(), method: "Payvit",
            nit: result.nit ?? "—", amount: interneTyped, statut: "complété", at: "Kounye a", fee: 0,
        })
        setInterneEmail(""); setInterneAmt("20")
        setMode(null); goStep(0)
    }

    /** ── Step 3 rows ───────────────────────────────────────────────────
     *  The ONLY thing that differs between transaction types. Everything
     *  around them — avatar, title, reference block, CTA — is one component
     *  shared by all of them.
     *
     *  Reads top-down as a sentence: which rail, whose account, to whom, on
     *  what number, evidenced how, how much, what type — then what lands. */
    const refPrefix = mode === "depo" ? "DEP" : mode === "retre" ? "TRF"
        : mode === "bridge" ? "BRG" : "BRI"
    const reference = React.useMemo(() => refFor(refPrefix), [mode])

    /* Proof is the row that always applies; the TXN appears only when one was
       actually given, so an optional field left blank does not leave a dash. */
    const evidenceRows: SumRow[] = verify.needsProof
        ? [
            { icon: "receipt", k: "Prèv", v: verify.proof?.name ?? "—" },
            ...(verify.hasTxn ? [{ icon: "shield", k: "Txn ID", v: verify.txn } as SumRow] : []),
          ]
        : [{ icon: "shield", k: "Verifikasyon", v: "Pa nesesè" }]

    /* Read-only. Step 3 states what was decided; the decision itself is made
       on step 2, where there is still something to do about it. */
    const vitRow: SumRow = {
        icon: "zap", k: "Aplike VIT",
        v: !vitOn || vitSpend < 1
            ? "Non"
            : vitMode === "reduce"
              ? `${vitSpend} VIT · −${fmt(payCur === "HTG" ? vitSaveUsd * rate : vitSaveUsd, payDp)} ${payCur} sou frè a`
              : `${vitSpend} VIT · +${fmt(boostInReceive, receiveDp)} ${receiveCur} pou resipyan an`,
    }

    const feeGrossInPay = payCur === "HTG" ? fee * rate : fee
    const amountRows: SumRow[] = [
        { icon: "download", k: "Ou voye", v: `${fmt(typed, payDp)} ${payCur}` },
        { icon: "wallet", k: "Frè Payvit", v: `${fmt(feeGrossInPay, payDp)} ${payCur}` },
    ]
    const tailRows = (tip: string): SumRow[] => [
        { icon: "flag", k: "Tip", v: tip },
        ...(note.trim() ? [{ icon: "receipt", k: "Nòt", v: note.trim() } as SumRow] : []),
        { icon: "wallet", k: "Total ou ap peye", v: `${fmt(typed + feeInPay, payDp)} ${payCur}` },
        { div: true },
        { icon: "arrowDown", k: "Yo resevwa", v: `${fmt(converted, receiveDp)} ${receiveCur}`, tone: "ok" as const },
    ]

    const sumRows: SumRow[] =
        mode === "depo"
            ? [
                { icon: "wallet", k: "Metòd", v: String(payProvider) },
                { icon: "user", k: "Nimewo ou", v: USER.phone ?? "—" },
                { icon: "send", k: "Voye bay", v: PAYVIT_ACCOUNT.name },
                { icon: "card", k: "Sou nimewo",
                  v: payProvider === "NatCash" ? PAYVIT_ACCOUNT.natcash : PAYVIT_ACCOUNT.moncash },
                ...evidenceRows, ...amountRows, vitRow, ...tailRows("Depo"),
              ]
        : mode === "retre"
            ? [
                { icon: "wallet", k: "Metòd", v: String(payProvider) },
                { icon: "user", k: "Kont ou", v: WALLET_HANDLE[payProvider] ?? "—" },
                { icon: "send", k: "Resipyan", v: recip?.label ?? "—" },
                { icon: "card", k: "Sou nimewo", v: recip?.handle ?? "—" },
                ...evidenceRows, ...amountRows, vitRow, ...tailRows("Retrè"),
              ]
        : [
                { icon: "wallet", k: "Soti nan", v: String(payProvider) },
                { icon: "send", k: "Ale sou", v: String(receiveProvider) },
                ...(isLocalBridge ? [{ icon: "shield", k: "Nimewo telefòn ou", v: myPhone.trim() || "—" } as SumRow] : []),
                { icon: "user", k: isLocalBridge ? "Nimewo benefisyè" : "Kont ki ap resevwa",
                  v: benef.trim() || "—" },
                ...(isLocalBridge ? [{ icon: "receipt", k: `QR kod ${bLocTo}`, v: bridgeQr?.name ?? "—" } as SumRow] : []),
                ...evidenceRows, ...amountRows, vitRow,
                ...tailRows(isLocalBridge ? "Bridge Lokal" : "Bridge Entènasyonal"),
              ]

    /** The swap button. On Depo/Retrè it flips the DIRECTION — which is what
     *  the live flow's swap does, and why the two cards trade places rather
     *  than trading contents. On Bridge there is no direction to flip, so the
     *  same button trades the two endpoints instead. Same control, same
     *  animation, the only thing that differs is what "reverse" means. */
    const swap = () => {
        setOpenPicker(null)
        if (!isBridge) {
            /* Swapping into Depo moves the international wallet to the RECEIVE
               side. Paypal can only be withdrawn from, never deposited into,
               so a Retrè-on-Paypal that gets swapped would otherwise land on
               an impossible transaction with no visible cause. Move it and say
               so rather than silently allowing or silently rewriting. */
            if (!depo && intl === "Paypal") {
                setIntl("Cashapp")
                say("Paypal se pou Retrè sèlman — nou mete Cashapp pou Depo a.")
            }
            setDepo((v) => !v)
            return
        }
        if (isLocalBridge) { setBLocFrom(bLocTo); setBLocTo(bLocFrom) }
        else {
            if (bIntFrom === "Paypal") { say("Paypal pa ka resevwa lajan."); return }
            setBIntFrom(bIntTo); setBIntTo(bIntFrom)
        }
    }

    /** Which options a side offers. Bridge draws both sides from one family;
     *  Depo/Retrè draws the rail side from RAILS and the other from the
     *  international list for the current direction. */
    const optionsFor = (which: "pay" | "receive"): Provider[] => {
        if (isLocalBridge) return BRIDGE_RAILS
        // Bridge Entènasyonal has an international wallet on BOTH ends, so the
        // pay side may offer Paypal and the receive side may not.
        if (isBridge) return which === "pay" ? INTLS : INTLS_DEPO
        const railSide = (which === "pay") === depo
        return railSide ? RAILS : intlsFor(depo)
    }

    const setProvider = (which: "pay" | "receive", v: Provider) => {
        if (isLocalBridge) {
            const r = v as Rail
            // Both ends of a local bridge cannot be the same wallet — picking
            // the other side's wallet swaps them rather than colliding.
            if (which === "pay") { if (r === bLocTo) setBLocTo(bLocFrom); setBLocFrom(r) }
            else { if (r === bLocFrom) setBLocFrom(bLocTo); setBLocTo(r) }
        } else if (isBridge) {
            const w = v as Intl
            // Displacing the pay-side value onto the receive side is how
            // Paypal could still end up receiving — swapping it in is the one
            // path the narrowed option list does not cover.
            if (which === "pay") {
                if (w === bIntTo) setBIntTo(bIntFrom === "Paypal" ? "Cashapp" : bIntFrom)
                setBIntFrom(w)
            } else { if (w === bIntFrom) setBIntFrom(bIntTo); setBIntTo(w) }
        } else {
            const railSide = (which === "pay") === depo
            if (railSide) setRail(v as Rail); else setIntl(v as Intl)
        }
        setOpenPicker(null)
    }

    const providerPill = (provider: Provider, which: "pay" | "receive") => (
        <motion.button
            layout={!reduced}
            transition={reduced ? { duration: 0 } : SPRING}
            type="button"
            className="pv-tc-pill"
            onClick={() => setOpenPicker((o) => (o === which ? null : which))}
            aria-expanded={openPicker === which}
            aria-label={`Chanje bous — kounye a ${provider}`}
        >
            <BrandMark provider={provider} size={24} />
            <span className="pv-tc-pillname">{provider}</span>
            <motion.span animate={{ rotate: openPicker === which ? 180 : 0 }}
                         transition={{ duration: 0.22, ease: EASE }} style={{ display: "flex" }}>
                <Icon name="chevronDown" size={16} color={C.textSub} />
            </motion.span>
        </motion.button>
    )

    const picker = (which: "pay" | "receive") => (
        <AnimatePresence initial={false}>
            {openPicker === which && (
                <motion.div key={`pk-${which}`}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.26, ease: EASE }}
                    style={{ overflow: "hidden" }}>
                    <div className="pv-tc-pickerwrap pv-tc-glass-lite">
                        <ProviderPicker
                            options={optionsFor(which)}
                            value={which === "pay" ? payProvider : receiveProvider}
                            onChange={(v) => setProvider(which, v)}
                            layoutId={`pv-tf-pf-${which}`}
                        />
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    )

    /** Written on change rather than on navigation — a tab that gets closed
     *  never navigates, so an on-navigation write is exactly the one that
     *  loses an accidental close. Debounced because it fires per keystroke.
     *
     *  `visibilitychange` forces a flush: it is the last event a closing or
     *  backgrounded tab reliably fires, and on iOS it is often the ONLY one
     *  (`beforeunload` does not fire when the app is swiped away). */
    const snapshot = React.useCallback((): TransferDraft => ({
        v: DRAFT_VERSION, savedAt: Date.now(), step, mode, direction: depo ? "depo" : "retre",
        rail, intl, amount: amt, note, recipientId: rid,
    }), [step, mode, depo, rail, intl, amt, note, rid])

    const worthKeeping = !!mode && (step > 0 || (!!amt && !!rid))

    React.useEffect(() => {
        if (!worthKeeping) return
        const t = window.setTimeout(() => saveDraft(userId, snapshot()), 400)
        return () => window.clearTimeout(t)
    }, [worthKeeping, snapshot, userId])

    React.useEffect(() => {
        const flush = () => {
            if (document.visibilityState === "hidden" && worthKeeping) {
                saveDraft(userId, snapshot())
            }
        }
        document.addEventListener("visibilitychange", flush)
        window.addEventListener("pagehide", flush)
        return () => {
            document.removeEventListener("visibilitychange", flush)
            window.removeEventListener("pagehide", flush)
        }
    }, [worthKeeping, snapshot, userId])

    /** Idempotency key survives retries of the SAME attempt (cleared only on
     *  confirmed success), matching the real TransfeFlow.tsx exactly — a
     *  retry after a network error collides with the original request at the
     *  backend rather than double-submitting. */
    const idemRef = React.useRef<string | null>(null)

    const submit = async () => {
        setBusy(true)
        const token = await getAccessToken()
        if (!token) {
            setBusy(false)
            say("Sesyon ekspire — konekte ankò.")
            return
        }
        if (!idemRef.current) idemRef.current = generateIdempotencyKey()
        const idempotency_key = idemRef.current
        const vit_spent_count = vitSpend > 0 ? vitSpend : undefined
        const vit_mode = vitSpend > 0 ? vitMode : undefined

        let result: Awaited<ReturnType<typeof submitWithdrawal>>
        if (isBridge) {
            if (isLocalBridge) {
                const direction = bLocFrom === "MonCash" ? "moncash_to_natcash" : "natcash_to_moncash"
                result = await submitBridgeTransfer({
                    direction, sender_phone: myPhone.trim(), receiver_phone: benef.trim(),
                    montant_htg: typed, proof_image: verify.proof?.dataUrl,
                    qr_code_image: bridgeQr?.dataUrl,
                    idempotency_key,
                }, token)
            } else {
                result = await submitRemittance({
                    pay_provider: bIntFrom, receive_provider: bIntTo, montant_pay: typed, pay_currency: "USD",
                    receiver_handle: benef.trim(), proof_image: verify.proof?.dataUrl,
                    client_note: `Bridge Entènasyonal · ${bIntFrom} → ${bIntTo}`,
                    idempotency_key, vit_spent_count, vit_mode,
                }, token)
            }
        } else {
            const isWithdrawal = payProvider === "Payvit" && (receiveProvider === "MonCash" || receiveProvider === "NatCash")
            if (isWithdrawal) {
                result = await submitWithdrawal({
                    methode: receiveProvider as "MonCash" | "NatCash", telephone: recip?.handle ?? "",
                    montant_usd: usd, idempotency_key, vit_spent_count, vit_mode,
                }, token)
            } else {
                result = await submitRemittance({
                    pay_provider: payProvider, receive_provider: receiveProvider,
                    montant_pay: typed, pay_currency: payCur as "USD" | "HTG",
                    receiver_name: recip?.label,
                    receiver_handle: receiveProvider === "Payvit" ? undefined : recip?.handle,
                    // Backend requires this specifically when receive_provider
                    // is "Payvit" — RecipSheet collects an email (not a phone)
                    // for exactly this case, stored in the same `handle` field.
                    receiver_payvit_email: receiveProvider === "Payvit" ? recip?.handle : undefined,
                    txn_id: verify.hasTxn ? verify.txn : undefined, proof_image: verify.proof?.dataUrl,
                    client_note: note.trim() || undefined,
                    idempotency_key, vit_spent_count, vit_mode,
                }, token)
            }
        }

        setBusy(false)
        if (!result.ok) {
            say(result.error ?? "Erè. Eseye ankò.")
            return
        }
        idemRef.current = null
        /** The row the popup tracks and the dashboard will highlight. Only
         *  `nit` is real — none of the three endpoints return a full row, so
         *  the rest stays client-synthesized, same as before. */
        onSubmitted({
            id: `new-${Date.now().toString(36)}`, seed: recip?.seed ?? "acct-new", kind: depo ? "depo" : "retre",
            name: recip?.label ?? "", handle: recip?.handle ?? "", method: recip?.provider ?? "Payvit",
            nit: result.nit ?? "—", amount: usd, statut: "en_attente", at: "Kounye a",
            note: note.trim() || undefined, fee: feeAfter, rate,
        })
        clearDraft(userId)
        verify.reset()
        setMode(null); goStep(0)
    }

    return (
        <>
            {!mode && (
                <TypeChooser
                    onPick={(k) => {
                        setMode(k)
                        const isDepo = k === "depo"
                        setDepo(isDepo)
                        // The default has to be denominated in the currency
                        // the route actually collects, or the summary reads
                        // 5,000 USD the moment the screen opens.
                        // Bridge moves gourdes between two local wallets, so
                        // it is denominated in HTG like a Depo; Retrè is the
                        // only USD-collected route.
                        const htgSide = isDepo || k === "bridge"
                        setAmt(htgSide ? "5000" : "50")
                        setRecvOverride(null)
                        setOpenPicker(null)
                        // Evidence is per-transaction. Carrying a TXN across a
                        // route change would let one payment vouch for another.
                        verify.reset()
                        setStep(0)
                    }}
                />
            )}
            {mode === "interne" && (
                <>
                    <StepRail step={step === 0 ? 0 : 2} />
                    <div className="steps-vp">
                        <motion.div key={step === 0 ? "d" : "k"}
                            initial={reduced ? { opacity: 0 } : { opacity: 0, x: travel * 30 }}
                            animate={{ opacity: 1, x: 0 }} transition={T.base} className="stack">
                            {step === 0 ? (
                                <>
                                    <section className="sec">
                                        <h2 className="sec-t">Imèl resipyan an</h2>
                                        <div className="field g g-chip">
                                            <Icon name="user" size={18} color={C.textMute} />
                                            <input value={interneEmail}
                                                   onChange={(e) => setInterneEmail(e.target.value)}
                                                   type="email" inputMode="email"
                                                   placeholder="imel@egzanp.com" aria-label="Imèl resipyan an" />
                                        </div>
                                        {interneEmail && !interneEmailValid && (
                                            <div className="vfy-err">Imèl sa a pa valid.</div>
                                        )}
                                        <p className="hint">Resipyan an dwe gen deja yon kont Payvit ak imèl sa a.</p>
                                    </section>
                                    <section className="sec">
                                        <h2 className="sec-t">Kantite (USD)</h2>
                                        <div className="chips">
                                            {["10", "20", "50", "100", "150"].map((v) => (
                                                <Press key={v} className={`chip g g-chip${interneAmt === v ? " on" : ""}`}
                                                       onClick={() => setInterneAmt(v)} scale={0.94}>
                                                    {fmt(Number(v), 0)}
                                                </Press>
                                            ))}
                                        </div>
                                        <div className="field g g-chip">
                                            <Icon name="wallet" size={18} color={C.textMute} />
                                            <input value={interneAmt}
                                                   onChange={(e) => setInterneAmt(e.target.value.replace(/[^\d.]/g, ""))}
                                                   inputMode="decimal" aria-label="Kantite an dola" />
                                            <span className="field-suffix">USD</span>
                                        </div>
                                        <p className="hint">Kantite a dwe ant $5 ak $200. Pa gen frè.</p>
                                    </section>
                                    <div className="btn-row">
                                        <Press className="cta cta-ghost" onClick={() => setMode(null)} aria-label="Chanje metòd">
                                            <Icon name="arrowLeft" size={17} color={C.text} />
                                        </Press>
                                        <Press className="cta" onClick={() => goStep(2)}
                                               disabled={!interneEmailValid || interneTyped < 5 || interneTyped > 200}>
                                            Kontinye <Icon name="arrowRight" size={18} color="#fff" stroke={2.2} />
                                        </Press>
                                    </div>
                                </>
                            ) : (
                                <KonfimeStep
                                    seed={`p2p-${interneEmail}`} name={interneEmail.split("@")[0] || "Resipyan"}
                                    rows={interneSumRows} reference={interneReference}
                                    onEdit={() => goStep(0)} onConfirm={interneSubmit} busy={interneBusy}
                                    say={say}
                                />
                            )}
                        </motion.div>
                    </div>
                </>
            )}
            {mode && mode !== "interne" && (
              <>
            {/* Step rail — the current step's chip is a shared element, so the
                progress marker travels rather than redrawing. */}
            <StepRail step={step} />

            {/* Keyed remount WITHOUT AnimatePresence, deliberately.
                
                Nesting a second `mode="wait"` inside the page-level one
                deadlocks: leave this flow mid-step and the outer transition
                waits forever on an inner exit that can never finish, because
                the element running it is being unmounted by the very
                transition that is waiting. The page after it never mounts.
                
                Remounting on `step` gives the incoming step its slide-in with
                no exit to wait on — which is what a native step flow does
                anyway. */}
            <div className="steps-vp">
                <motion.div
                    key={step}
                    initial={reduced ? { opacity: 0 } : { opacity: 0, x: travel * 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={T.base}
                    className="stack"
                >
                    {step === 0 && (
                        <>
                            {/* Converter. The two cards swap position on
                                direction change via layoutId, so the swap is
                                one object moving rather than two redrawing. */}
                            <div className="pv-tc-stack">
                                <div className="pv-tc-slot pv-tc-slot-pay">
                                    <motion.div layout={!reduced}
                                        transition={reduced ? { duration: 0 } : SPRING}
                                        className="pv-tc-card pv-tc-glass">
                                        <div className="pv-tc-cardhead">
                                            <span className="pv-tc-label">Wap Peye</span>
                                        </div>
                                        <div className="pv-tc-cardbody">
                                            <input className="pv-tc-amount" value={amt}
                                                onChange={(e) => { setAmt(e.target.value.replace(/[^\d.]/g, "")); setRecvOverride(null) }}
                                                placeholder="0" inputMode="decimal"
                                                aria-label={`Kantite an ${payCur}`} />
                                            {providerPill(payProvider, "pay")}
                                        </div>
                                        <div className="pv-tc-cardfoot">
                                            <span className="pv-tc-foothint">
                                                {payProvider === "Payvit" ? "Balans Payvit"
                                                    : isBridge ? "Sou kont sa a"
                                                    : depo ? "Nan men w" : "Kont entènasyonal"}
                                            </span>
                                            <span className="pv-tc-footval pv-tc-num">
                                                {typed > 0 ? `${fmt(typed, payDp)} ${payCur}` : `0 ${payCur}`}
                                            </span>
                                        </div>
                                    </motion.div>
                                    {picker("pay")}
                                </div>

                                <div className="pv-tc-swaprow">
                                    <div className="pv-tc-swapline" />
                                    <motion.button type="button" className="pv-tc-swapbtn" onClick={swap}
                                        whileTap={reduced ? undefined : { scale: 0.9 }}
                                        whileHover={reduced ? undefined : { scale: 1.06 }}
                                        transition={{ duration: 0.18, ease: EASE }}
                                        aria-label={isBridge ? "Vire de kont yo"
                                            : depo ? "Chanje pou Retrè" : "Chanje pou Depo"}>
                                        <motion.span
                                            animate={{ rotate: (isBridge ? payProvider === receiveProvider : depo) ? 0 : 180 }}
                                            transition={reduced ? { duration: 0 } : SPRING}
                                            style={{ display: "flex" }}>
                                            <Icon name="swap" size={20} color="#fff" stroke={1.9} />
                                        </motion.span>
                                    </motion.button>
                                    <div className="pv-tc-swapline" />
                                </div>

                                <div className="pv-tc-slot pv-tc-slot-receive">
                                    <motion.div layout={!reduced}
                                        transition={reduced ? { duration: 0 } : SPRING}
                                        className="pv-tc-card pv-tc-glass">
                                        <div className="pv-tc-cardhead">
                                            <span className="pv-tc-label">Wap Resevwa</span>
                                        </div>
                                        <div className="pv-tc-cardbody">
                                            <input
                                                className="pv-tc-amount pv-tc-amount-out"
                                                value={recvOverride ?? (typed > 0 ? converted.toFixed(receiveDp) : "")}
                                                onChange={(e) => {
                                                    const raw = e.target.value.replace(/[^\d.]/g, "")
                                                    setRecvOverride(raw)
                                                    /** Solve backwards for what the PAY side must be to produce this
                                                     *  receive amount — inverse of `convertedBase`, net of any VIT
                                                     *  boost currently applied, using whichever rate this route is
                                                     *  actually on (Depo vs Retrè carry different rates, both the
                                                     *  live ones from service-status, not the fixture fallback). */
                                                    const desiredConverted = parseFloat(raw) || 0
                                                    const desiredBase = desiredConverted - boostInReceive
                                                    const nextTyped = isBridge ? desiredBase
                                                        : depo ? desiredBase * RATE_DEPO : desiredBase / RATE_RETRE
                                                    setAmt(nextTyped > 0 ? nextTyped.toFixed(payDp) : "")
                                                }}
                                                placeholder="0" inputMode="decimal"
                                                aria-label={`Kantite an ${receiveCur}`} />
                                            {providerPill(receiveProvider, "receive")}
                                        </div>
                                        <div className="pv-tc-cardfoot">
                                            <span className="pv-tc-foothint">
                                                {isBridge ? "Sou kont ki ap resevwa a"
                                                    : depo ? "Sou kont entènasyonal ou" : "Sou telefòn nan"}
                                            </span>
                                            <span className="pv-tc-footval pv-tc-num">
                                                {typed > 0
                                                    ? `${fmt(converted, receiveDp)} ${receiveCur}`
                                                    : `0 ${receiveCur}`}
                                            </span>
                                        </div>
                                    </motion.div>
                                    {picker("receive")}
                                </div>
                            </div>

                            <div className="pv-tc-rate">
                                <Icon name="info" size={16} color={C.tealDeep} />
                                <AnimatePresence mode="wait" initial={false}>
                                    <motion.span
                                        key={isBridge ? `bridge-${payCur}` : String(rate)}
                                        initial={{ opacity: 0, y: 3 }} animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -3 }}
                                        transition={{ duration: 0.18, ease: EASE }} className="pv-tc-num">
                                        {isBridge
                                            ? `Menm lajan — ${payCur} → ${receiveCur}`
                                            : `1 USD = ${rate} HTG`}
                                    </motion.span>
                                </AnimatePresence>
                                <span className="pv-tc-ratetag">
                                    {isBridge ? (isLocalBridge ? "Bridge" : "Bridge Intl") : depo ? "Depo" : "Retrè"}
                                </span>
                            </div>

                            {/* Step 2 only. On Detay there is nothing settled
                                to summarise, and a running total beside the
                                fields the user is still filling reads as
                                noise. Conditionally RENDERED, not hidden —
                                nothing of it is in the DOM on step 1. */}

                            {/* Bridge doesn't use `recip`/`rid` at all — its own
                                beneficiary field further down (`benef`) is the
                                real recipient capture for both bridge variants,
                                so this section (and the `recip` it produces)
                                would be dead, confusing UI here. */}
                            {!isBridge && (
                                <section className="sec">
                                    <h2 className="sec-t">Resipyan</h2>
                                    <div className="bubs">
                                        {recipientsForReceive.map((r) => (
                                            <Press key={r.id} className="bub" onClick={() => setRid(r.id)} scale={0.93}>
                                                <span className="bub-av">
                                                    {rid === r.id && (
                                                        <motion.span layoutId="bub-ring" className="bub-ring" transition={SPRING} />
                                                    )}
                                                    <Avatar seed={r.seed} name={r.label} size={54} />
                                                </span>
                                                <span className="bub-n">{r.label.split(" ")[0]}</span>
                                            </Press>
                                        ))}
                                        <Press className="bub" onClick={() => setSheet(true)} scale={0.93}>
                                            <span className="bub-av bub-add"><Icon name="plus" size={20} color={C.textMute} /></span>
                                            <span className="bub-n">Nouvo</span>
                                        </Press>
                                    </div>
                                </section>
                            )}

                            <section className="sec">
                                <h2 className="sec-t">Kantite ({payCur})</h2>
                                <div className="chips">
                                    {/* Quick-select follows the active CURRENCY, not the
                                        direction — Bridge Lokal is HTG with depo=false, and
                                        keying off depo showed it dollar presets. */}
                                    {(payCur === "HTG"
                                        ? ["1000", "2500", "5000", "10000", "20000"]
                                        : ["10", "25", "50", "100", "150"]).map((v) => (
                                        <Press key={v} className={`chip g g-chip${amt === v ? " on" : ""}`} onClick={() => { setAmt(v); setRecvOverride(null) }} scale={0.94}>
                                            {fmt(Number(v), 0)}
                                        </Press>
                                    ))}
                                </div>
                                <div className="field g g-chip">
                                    <Icon name="wallet" size={18} color={C.textMute} />
                                    <input value={amt} onChange={(e) => { setAmt(e.target.value.replace(/[^\d.]/g, "")); setRecvOverride(null) }}
                                           inputMode="decimal" aria-label="Kantite" />
                                    <span className="field-suffix">{payCur}</span>
                                </div>
                            </section>

                            {/* ── Fields that belong to THIS type only ──────
                                Addresses only. Verification — the TXN number
                                and the proof upload — lives on step 2 and
                                nowhere else: asking for evidence of a payment
                                on the screen where the user is still deciding
                                the amount put the question before the act it
                                is asking about. */}
                            {mode === "bridge" && (
                                <section className="sec">
                                    <h2 className="sec-t">Nimewo telefòn ou</h2>
                                    <div className="field g g-chip">
                                        <Icon name="user" size={18} color={C.textMute} />
                                        <input
                                            value={myPhone}
                                            onChange={(e) => setMyPhone(e.target.value)}
                                            inputMode="tel"
                                            placeholder="+509 38 12 3456"
                                            aria-label="Nimewo telefòn ou"
                                        />
                                    </div>
                                    <p className="hint">Sa a se nimewo w ap peye a, sou {bLocFrom}.</p>
                                </section>
                            )}
                            {(mode === "bridge" || mode === "bridge-intl") && (
                                <section className="sec">
                                    <h2 className="sec-t">
                                        {mode === "bridge" ? "Nimewo benefisyè a" : "Kont ki ap resevwa a"}
                                    </h2>
                                    <div className="field g g-chip">
                                        <Icon name="user" size={18} color={C.textMute} />
                                        <input
                                            value={benef}
                                            onChange={(e) => setBenef(e.target.value)}
                                            inputMode={mode === "bridge" ? "tel" : "text"}
                                            placeholder={mode === "bridge" ? "+509 38 12 3456" : "@non-itilizatè"}
                                            aria-label="Kont benefisyè"
                                        />
                                    </div>
                                    <p className="hint">
                                        {mode === "bridge"
                                            ? "Frè bridge la se 125 HTG, an plis frè Payvit la."
                                            : "Toude kont yo dwe se pa w. Bridge entènasyonal pa gen frè lokal."}
                                    </p>
                                </section>
                            )}
                            {mode === "bridge" && (
                                <section className="sec">
                                    <h2 className="sec-t">QR kod {bLocTo} resipyan an</h2>
                                    <input
                                        ref={bridgeQrFileRef}
                                        type="file"
                                        accept="image/png,image/jpeg,image/webp"
                                        hidden
                                        onChange={(e) => {
                                            const file = e.target.files?.[0]
                                            e.target.value = ""
                                            setBridgeQrError(null)
                                            if (!file) return
                                            if (!file.type.startsWith("image/")) {
                                                setBridgeQrError("Chwazi yon imaj (PNG, JPG oswa WEBP).")
                                                return
                                            }
                                            if (file.size > PROOF_MAX_BYTES) {
                                                setBridgeQrError(`Fichye a twò gwo (${(file.size / 1048576).toFixed(1)} Mo). Maksimòm se 8 Mo.`)
                                                return
                                            }
                                            const reader = new FileReader()
                                            reader.onload = () => setBridgeQr({ name: file.name, dataUrl: String(reader.result) })
                                            reader.onerror = () => setBridgeQrError("Nou pa t kapab li fichye a. Eseye ankò.")
                                            reader.readAsDataURL(file)
                                        }}
                                    />
                                    <div className="drop g g-chip vfy-drop" data-has={!!bridgeQr}
                                         role="button" tabIndex={0}
                                         onClick={() => bridgeQrFileRef.current?.click()}
                                         onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); bridgeQrFileRef.current?.click() } }}>
                                        <Icon name={bridgeQr ? "check" : "receipt"} size={20}
                                              color={bridgeQr ? C.ok : C.teal} stroke={bridgeQr ? 2.6 : 1.6} />
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                            <div className="drop-t">{bridgeQr ? bridgeQr.name : `Kole/telechaje QR kod ${bLocTo} la`}</div>
                                            <div className="drop-s">
                                                {bridgeQr
                                                    ? "Tape pou chanje l"
                                                    : `Louvri aplikasyon ${bLocTo} resipyan an, pran QR kod pou resevwa lajan an, epi telechaje l isit la.`}
                                            </div>
                                        </div>
                                        {bridgeQr && (
                                            <Press className="vfy-x" scale={0.9} aria-label="Retire QR kod la"
                                                   onClick={(e: any) => { e.stopPropagation(); setBridgeQr(null) }}>
                                                <Icon name="x" size={15} color={C.textMute} stroke={2.2} />
                                            </Press>
                                        )}
                                    </div>
                                    {bridgeQrError && <div className="vfy-err">{bridgeQrError}</div>}
                                </section>
                            )}
                            <section className="sec">
                                <h2 className="sec-t">Ajoute yon nòt (opsyonèl)</h2>
                                <div className="field g g-chip">
                                    <Icon name="receipt" size={18} color={C.textMute} />
                                    <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Pou lekòl la, pou manman…" />
                                </div>
                            </section>

                            <div className="btn-row">
                                <Press className="cta cta-ghost" onClick={() => setMode(null)} aria-label="Chanje metòd">
                                    <Icon name="arrowLeft" size={17} color={C.text} />
                                </Press>
                                <Press className="cta" onClick={() => goStep(1)}
                                       disabled={(isBridge ? !benef.trim() : !recip) || !typed
                                           || (mode === "bridge" && (!bridgeQr || myPhone.trim().length < 8))}>
                                    Kontinye <Icon name="arrowRight" size={18} color="#fff" stroke={2.2} />
                                </Press>
                            </div>
                        </>
                    )}

                    {/* ── STEP 2 · VERIFYE — the gate ────────────────────
                        The whole of verification lives here: where to send
                        the money, the TXN it came back with, and the proof.
                        Nothing advances until both are in hand. */}
                    {step === 1 && (
                        <>
                            <PayBrief
                                amount={typed}
                                feeGross={payCur === "HTG" ? fee * rate : fee}
                                feeNet={feeInPay}
                                cur={payCur} dp={payDp}
                                vitOn={vitOn} setVitOn={setVitOn} vitSpend={vitSpend} vitCount={vitCount}
                                vitMode={vitMode} setVitMode={setVitMode}
                                boost={boostInReceive} boostCur={receiveCur} boostDp={receiveDp}
                            />
                            <VerifyBox source={payProvider} v={verify} />
                            <Blocked missing={verify.missing} />
                            <div className="btn-row">
                                <Press className="cta cta-ghost" onClick={() => goStep(0)}>Retounen</Press>
                                <Press className="cta" onClick={() => goStep(2)} disabled={!verify.ok}>
                                    Kontinye <Icon name="arrowRight" size={18} color="#fff" stroke={2.2} />
                                </Press>
                            </div>
                        </>
                    )}

                    {/* ── STEP 3 · KONFIME ────────────────────────────────
                        One frame for every type; only `sumRows` differs. */}
                    {step === 2 && (
                        <KonfimeStep
                            seed={mode === "retre" ? (recip?.seed ?? USER.seed) : USER.seed}
                            name={mode === "retre" ? (recip?.label ?? USER.name) : USER.name}
                            rows={sumRows}
                            reference={reference}
                            onEdit={() => goStep(0)}
                            onConfirm={submit}
                            busy={busy}
                            say={say}
                            blocked={verify.missing}
                        />
                    )}
                </motion.div>
            </div>

              </>
            )}
            <RecipSheet open={sheet} onClose={() => setSheet(false)} provider={receiveProvider}
                onSave={async (draft) => {
                    const token = await getAccessToken()
                    if (!token) { say("Sesyon ekspire. Konekte ankò."); return false }
                    const result = await submitRecipient(draft, token)
                    if (!result.ok) { say(result.error); return false }
                    const r: SavedRecipient = {
                        id: result.recipient.id, seed: `rec-${result.recipient.id}`,
                        provider: draft.provider, label: draft.label, handle: draft.handle,
                    }
                    const updated = [...recipients, r]
                    setRecipients(updated)
                    saveRecipients(userId, updated)
                    setRid(r.id)
                    setSheet(false)
                    say("Resipyan sere")
                    return true
                }} />
        </>
    )
}

/** The copy pattern, in one place. Same two moves the Konfime step already
 *  made — write to the clipboard, say so — plus a tick on the control itself
 *  for the two seconds after, so the confirmation is where the finger is and
 *  not only in a toast at the other end of the screen. */
function useCopy(say: (m: string) => void) {
    const [copied, setCopied] = React.useState(false)
    const timer = React.useRef<number | undefined>(undefined)
    React.useEffect(() => () => window.clearTimeout(timer.current), [])
    const copy = React.useCallback((text: string, msg = "Nimewo kopye") => {
        try { navigator.clipboard?.writeText(text) } catch (_e) {}
        say(msg)
        setCopied(true)
        window.clearTimeout(timer.current)
        timer.current = window.setTimeout(() => setCopied(false), 2000)
    }, [say])
    return { copied, copy }
}

function RevRow({ k, v, tone, big }: { k: string; v: string; tone?: "ok"; big?: boolean }) {
    return (
        <div className={`rev-row${big ? " big" : ""}`}>
            <span className="rev-k">{k}</span>
            <span className="rev-v n" style={tone === "ok" ? { color: C.ok } : undefined}>{v}</span>
        </div>
    )
}

function Success({ amount, recip, onAgain }: { amount: string; recip: string; onAgain: () => void }) {
    const reduced = useReduced()
    return (
        <div className="succ g g-card">
            <motion.span className="succ-ring"
                initial={reduced ? false : { scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }} transition={SPRING}>
                <motion.span initial={reduced ? false : { pathLength: 0 }} animate={{ pathLength: 1 }}>
                    <Icon name="check" size={30} color="#fff" stroke={2.8} />
                </motion.span>
            </motion.span>
            <h2 className="succ-t">Demann lan pati!</h2>
            <p className="succ-p">
                Demann lan ale ak siksè, nou pral trete l sou plas. Verifye imèl ou pou resi a — ou ka
                swiv tranzaksyon an depi sou tablodbò w la.
            </p>
            <div className="succ-rows">
                <div className="rev-row"><span className="rev-k">Resipyan</span><span className="rev-v">{recip}</span></div>
                <div className="rev-row"><span className="rev-k">Ou voye</span><span className="rev-v n">{amount}</span></div>
                <div className="rev-row"><span className="rev-k">NIT</span><span className="rev-v n">TRF-MSAN9U-DOHL</span></div>
            </div>
            <Press className="cta" onClick={onAgain}>Fè yon lòt tranzaksyon</Press>
        </div>
    )
}

// ═══════════════════════════════════════════════════════════════════════════
// KAT · ISTORIK · PROFIL · SHEETS
// ═══════════════════════════════════════════════════════════════════════════
// ── Shared file-to-base64 picker, matching the pattern already used for
//    transaction proofs and the Bridge Lokal QR upload ─────────────────────
function readImageAsDataUrl(
    file: File | null | undefined,
    onError: (msg: string) => void,
    onDone: (v: { name: string; dataUrl: string }) => void
) {
    if (!file) return
    if (!file.type.startsWith("image/")) { onError("Chwazi yon imaj (PNG, JPG oswa WEBP)."); return }
    if (file.size > PROOF_MAX_BYTES) { onError(`Fichye a twò gwo (${(file.size / 1048576).toFixed(1)} Mo). Maksimòm se 8 Mo.`); return }
    const reader = new FileReader()
    reader.onload = () => onDone({ name: file.name, dataUrl: String(reader.result) })
    reader.onerror = () => onError("Nou pa t kapab li fichye a. Eseye ankò.")
    reader.readAsDataURL(file)
}

function KatDropzone({ label, hint, value, error, onPick }: {
    label: string; hint: string; value: { name: string } | null; error: string | null
    onPick: (f: File | null | undefined) => void
}) {
    const ref = React.useRef<HTMLInputElement | null>(null)
    return (
        <section className="sec">
            <h2 className="sec-t">{label} <span className="vfy-star" aria-label="obligatwa">*</span></h2>
            <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" hidden
                   onChange={(e) => { onPick(e.target.files?.[0]); e.target.value = "" }} />
            <div className="drop g g-chip vfy-drop" data-has={!!value} role="button" tabIndex={0}
                 onClick={() => ref.current?.click()}
                 onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); ref.current?.click() } }}>
                <Icon name={value ? "check" : "receipt"} size={20} color={value ? C.ok : C.teal} stroke={value ? 2.6 : 1.6} />
                <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="drop-t">{value ? value.name : hint}</div>
                    {!value && <div className="drop-s">Maksimòm 8 Mo.</div>}
                </div>
            </div>
            {error && <div className="vfy-err">{error}</div>}
        </section>
    )
}

/** Card request + top-up form. `mode="create"` (needs card_name + an ID
 *  document, min $25) or `mode="topup"` (no name/ID, min $5) — the two
 *  request/accept shapes WaldenPayCardService mirrors deliberately, so this
 *  one form covers both rather than duplicating it. */
function CardRequestForm({ mode, getAccessToken, say, onSubmitted }: {
    mode: "create" | "topup"; getAccessToken: () => Promise<string | null>
    say: (m: string) => void; onSubmitted: () => void
}) {
    const [cardName, setCardName] = React.useState("")
    const [amount, setAmount] = React.useState(mode === "create" ? "25" : "10")
    const [method, setMethod] = React.useState<"MonCash" | "NatCash">("MonCash")
    const [payerPhone, setPayerPhone] = React.useState("")
    const [txnId, setTxnId] = React.useState("")
    const [declaredHtg, setDeclaredHtg] = React.useState("")
    const [proof, setProof] = React.useState<{ name: string; dataUrl: string } | null>(null)
    const [proofError, setProofError] = React.useState<string | null>(null)
    const [idDoc, setIdDoc] = React.useState<{ name: string; dataUrl: string } | null>(null)
    const [idDocError, setIdDocError] = React.useState<string | null>(null)
    const [quote, setQuote] = React.useState<CardQuote | CardTopupQuote | null>(null)
    const [busy, setBusy] = React.useState(false)
    const idemRef = React.useRef<string | null>(null)

    const typed = parseFloat(amount) || 0
    const minAmount = mode === "create" ? 25 : 5

    React.useEffect(() => {
        if (typed < minAmount) { setQuote(null); return }
        let live = true
        const t = window.setTimeout(async () => {
            const token = await getAccessToken()
            if (!token || !live) return
            const q = mode === "create" ? await fetchCardQuote(typed, token) : await fetchCardTopupQuote(typed, token)
            if (live) setQuote(q)
        }, 350)
        return () => { live = false; window.clearTimeout(t) }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [typed, mode])

    // Declared HTG tracks the live quote's total dynamically — it re-syncs
    // every time the requested amount changes (a manual edit only ever
    // applied to the OLD total, so carrying it forward across a new amount
    // would silently understate/overstate what's actually owed). A manual
    // edit still sticks for as long as the amount itself doesn't change —
    // useful since a rail sometimes only accepts whole HTG and the computed
    // total isn't one.
    const declaredTouched = React.useRef(false)
    React.useEffect(() => { declaredTouched.current = false }, [typed])
    React.useEffect(() => {
        if (quote && !declaredTouched.current) setDeclaredHtg(String(quote.total_htg))
    }, [quote])

    const canSubmit = !busy && typed >= minAmount && !!proof && (mode === "topup" || (cardName.trim() && !!idDoc))
        && parseFloat(declaredHtg) > 0

    const submit = async () => {
        if (!canSubmit) return
        setBusy(true)
        const token = await getAccessToken()
        if (!token) { setBusy(false); say("Sesyon ekspire — konekte ankò."); return }
        if (!idemRef.current) idemRef.current = generateIdempotencyKey()
        const idempotency_key = idemRef.current

        const result = mode === "create"
            ? await submitCardCreate({
                card_name: cardName.trim(), requested_balance_usd: typed, payment_method: method,
                payer_phone: payerPhone.trim() || undefined, txn_id: txnId || undefined,
                proof_image: proof!.dataUrl, id_document_image: idDoc!.dataUrl,
                declared_amount_htg: parseFloat(declaredHtg), idempotency_key,
            }, token)
            : await submitCardTopup({
                base_amount_usd: typed, payment_method: method, payer_phone: payerPhone.trim() || undefined,
                txn_id: txnId || undefined, proof_image: proof!.dataUrl,
                declared_amount_htg: parseFloat(declaredHtg), idempotency_key,
            }, token)

        setBusy(false)
        if (!result.ok) { say(result.error ?? "Erè. Eseye ankò."); return }
        idemRef.current = null
        say(mode === "create" ? "Demann kat la voye — n ap tcheke peman an." : "Demann rechaj la voye — n ap tcheke peman an.")
        onSubmitted()
    }

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: S[5] }}>
            {mode === "create" && (
                <section className="sec">
                    <h2 className="sec-t">Non sou kat la</h2>
                    <div className="field g g-chip">
                        <Icon name="user" size={18} color={C.textMute} />
                        <input value={cardName} onChange={(e) => setCardName(e.target.value)} placeholder="Non konplè" aria-label="Non sou kat la" />
                    </div>
                </section>
            )}

            <section className="sec">
                <h2 className="sec-t">{mode === "create" ? "Balans ou vle sou kat la (USD)" : "Kantite pou rechaje (USD)"}</h2>
                <div className="field g g-chip">
                    <Icon name="wallet" size={18} color={C.textMute} />
                    <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
                           inputMode="decimal" aria-label="Kantite an dola" />
                    <span className="field-suffix">USD</span>
                </div>
                <p className="hint">Minimòm ${fmt(minAmount, 0)} USD.</p>
            </section>

            {quote && (
                <div className="live g g-card">
                    <span className="lbl">Total pou peye</span>
                    <div className="live-big n">{fmt(quote.total_usd, 2)}<span> USD</span></div>
                    <div className="live-rows">
                        <div className="live-row"><span>Frè</span><span className="n">{fmt(quote.fee_usd, 2)} USD</span></div>
                        <div className="live-row total"><span>An HTG</span><span className="n">{fmt(quote.total_htg, 0)} HTG</span></div>
                    </div>
                </div>
            )}

            <section className="sec">
                <h2 className="sec-t">Peye avèk</h2>
                <div className="pv-tc-pickerwrap pv-tc-glass-lite">
                    <ProviderPicker options={["MonCash", "NatCash"] as Rail[]} value={method}
                                    onChange={(v) => setMethod(v as "MonCash" | "NatCash")} layoutId="kat-pf" />
                </div>
                {quote && (
                    <p className="hint">
                        Voye sou {method === "MonCash" ? quote.account_moncash : quote.account_natcash}.
                    </p>
                )}
            </section>

            <section className="sec">
                <h2 className="sec-t">Nimewo TXN <span className="vfy-opt">opsyonèl</span></h2>
                <label className="field g g-chip vfy-txn">
                    <Icon name="shield" size={18} color={C.textMute} />
                    <input value={txnId} onChange={(e) => setTxnId(e.target.value.replace(/\D/g, "").slice(0, 12))}
                           inputMode="numeric" placeholder="123456789012" aria-label="Nimewo TXN" style={{ letterSpacing: ".06em" }} />
                    <span className="vfy-count n" data-ok={/^\d{12}$/.test(txnId)}>{txnId.length}/12</span>
                </label>
            </section>

            <section className="sec">
                <h2 className="sec-t">Kantite ou deklare peye (HTG)</h2>
                <div className="field g g-chip">
                    <Icon name="wallet" size={18} color={C.textMute} />
                    <input value={declaredHtg}
                           onChange={(e) => { declaredTouched.current = true; setDeclaredHtg(e.target.value.replace(/[^\d.]/g, "")) }}
                           inputMode="decimal" aria-label="Kantite deklare an HTG" />
                    <span className="field-suffix">HTG</span>
                </div>
            </section>

            <KatDropzone label="Prèv peman an" hint="Mete yon foto konfimasyon peman an" value={proof} error={proofError}
                         onPick={(f) => readImageAsDataUrl(f, setProofError, setProof)} />

            {mode === "create" && (
                <KatDropzone label="Dokiman idantite" hint="Paspò, kat idantite oswa lisans"
                             value={idDoc} error={idDocError} onPick={(f) => readImageAsDataUrl(f, setIdDocError, setIdDoc)} />
            )}

            <Press className="cta" disabled={!canSubmit} onClick={submit}>
                {busy ? <><span className="spin" /> N ap voye…</> : <>Voye demann lan <Icon name="send" size={17} color="#fff" /></>}
            </Press>
        </div>
    )
}

/** Placeholder shown while `CARD_FEATURE_LIVE` is `false` — see the switch
 *  at the top of this file. Fills the same tab slot the real card flow
 *  would, so the nav/dock/hero entry points into "Kat" all still work,
 *  they just land here instead of the live flow.
 *
 *  Reuses the REAL card face markup (`.kat-scene`/`.kat-face`, the same
 *  classes the live "Kat Mwen" screen renders — see `Kat()` below) under a
 *  frosted "byento" cover, the way a product teaser shows the actual
 *  design rather than a generic icon. The announcement block underneath
 *  reuses `.succ`/`.succ-ring`/`.succ-t`/`.succ-p` — the app's own existing
 *  "big centered announcement" pattern (currently used for a completed
 *  transfer), not a new visual style invented just for this. */
function KatComingSoon() {
    const reduced = useReduced()
    return (
        <div className="stack">
            <div style={{ display: "flex", alignItems: "center", gap: S[3] }}>
                <span className="row-ico g g-chip"><Icon name="card" size={18} color={C.teal} /></span>
                <h1 className="hdr-t">Kat Mwen</h1>
            </div>

            <motion.div className="kat-scene"
                initial={reduced ? false : { opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={T.slow}>
                <div className="kat-face kat-front g g-dark" style={{ cursor: "default" }}>
                    <div className="kat-top" style={{ opacity: 0.4 }}>
                        <span className="kat-chip">Payvit</span>
                    </div>
                    <div className="kat-pan n" style={{ opacity: 0.4 }}>•••• •••• •••• ••••</div>
                    <div className="kat-bot" style={{ opacity: 0.4 }}>
                        <div className="kat-nm">OU MENM</div>
                        <div className="kat-meta">
                            <span><i>Ekspirasyon</i><b className="n">••/••</b></span>
                            <span><i>CVV</i><b className="n">•••</b></span>
                        </div>
                    </div>
                    <div className="kat-brand" style={{ opacity: 0.4 }}><PayvitLogo size={20} /> Payvit</div>

                    {/* The actual cover — everything above is the real card
                        design, just dimmed underneath this frost. */}
                    <div style={{
                        position: "absolute", inset: 0,
                        background: "rgba(7,40,38,.42)", backdropFilter: "blur(1.5px)",
                    }} />
                </div>
            </motion.div>

            <motion.section className="succ g g-card"
                initial={reduced ? false : { opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...T.slow, delay: reduced ? 0 : 0.08 }}>
                <span className="succ-ring">
                    <Icon name="hourglass" size={26} color="#fff" stroke={2} />
                </span>
                <span className="lbl">Nouvo fonksyon</span>
                <h2 className="succ-t">Kat vityèl Payvit la ap vini byento</h2>
            </motion.section>
        </div>
    )
}

function Kat({ say, user: USER, getAccessToken }: {
    say: (m: string) => void; user: { seed: string; name: string; email: string; phone: string | null }
    getAccessToken: () => Promise<string | null>
}) {
    const reduced = useReduced()
    const [loaded, setLoaded] = React.useState(false)
    const [status, setStatus] = React.useState<CardStatus | null>(null)
    const [showTopup, setShowTopup] = React.useState(false)

    const refresh = React.useCallback(async () => {
        if (!CARD_FEATURE_LIVE) { setLoaded(true); return }
        const token = await getAccessToken()
        if (!token) { setLoaded(true); return }
        const s = await fetchCardStatus(token)
        setStatus(s)
        setLoaded(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    React.useEffect(() => { refresh() }, [refresh])

    // ── Active card: number/CVV revealed on demand only, via the
    //    heavier-throttled /client/card/details endpoint — never fetched
    //    automatically just from opening this tab. ────────────────────────
    const [show, setShow] = React.useState(false)
    const [revealing, setRevealing] = React.useState(false)
    const [details, setDetails] = React.useState<any | null>(null)
    const [freezing, setFreezing] = React.useState(false)
    const hideTimer = React.useRef<number | null>(null)

    const reveal = async () => {
        if (show) { setShow(false); setDetails(null); if (hideTimer.current) window.clearTimeout(hideTimer.current); return }
        setRevealing(true)
        const token = await getAccessToken()
        const result = token ? await fetchCardDetails(token) : { ok: false, error: "Sesyon ekspire." }
        setRevealing(false)
        if (!result.ok) { say(result.error ?? "Nou pa t kapab montre kat la."); return }
        setDetails(result.raw)
        setShow(true)
        hideTimer.current = window.setTimeout(() => { setShow(false); setDetails(null) }, 20000)
    }
    React.useEffect(() => () => { if (hideTimer.current) window.clearTimeout(hideTimer.current) }, [])

    // WaldenPay's exact field names for a populated card were never
    // confirmed live against this session (see WaldenPayCardService.php's
    // own comments) — tried defensively across the plausible spellings
    // rather than asserted as fact.
    const cardNumber = details?.cardNumber ?? details?.card_number ?? details?.pan ?? null
    const cvv = details?.cvv ?? details?.cvc ?? details?.securityCode ?? null
    const expiry = details?.expiryDate ?? details?.expiry ?? details?.exp ?? null

    const toggleFreeze = async () => {
        setFreezing(true)
        const token = await getAccessToken()
        const result = token ? await submitCardFreeze(status?.status === "frozen" ? "unfreeze" : "freeze", token) : { ok: false, error: "Sesyon ekspire." }
        setFreezing(false)
        say(result.error ?? (result.ok ? "Fèt." : "Erè."))
        if (result.ok) refresh()
    }

    if (!CARD_FEATURE_LIVE) {
        return <KatComingSoon />
    }

    if (!loaded) {
        return (
            <div className="stack">
                <Skeleton h={200} r={R.lg} />
                <Skeleton h={80} r={R.md} />
            </div>
        )
    }

    // ── No card yet — or the previous request needs a fresh one ─────────
    if (!status || status.status === "rejected" || status.status === "refunded") {
        return (
            <>
                {status?.status === "rejected" && (
                    <div className="blocked">
                        <Icon name="alert" size={16} color={C.danger} />
                        <span>Demann kat ou pi bonè a te rejte{status.admin_note ? ` — ${status.admin_note}` : "."}</span>
                    </div>
                )}
                <section className="sec">
                    <h2 className="sec-t">Mande yon kat vityèl</h2>
                    <p className="hint">
                        Peye yon fwa pou aktive yon kat vityèl ak balans inisyal ke oumenm ap chwazi.
                    </p>
                </section>
                <CardRequestForm mode="create" getAccessToken={getAccessToken} say={say} onSubmitted={refresh} />
            </>
        )
    }

    // ── Awaiting admin review or awaiting on-chain deposit confirmation ──
    if (status.status === "submitted" || status.status === "awaiting_deposit") {
        return (
            <div className="stack">
                <div className="blocked" style={{ background: "rgba(26,157,139,.1)", borderColor: "rgba(26,157,139,.25)", color: C.tealDeep }}>
                    <Icon name="hourglass" size={16} color={C.tealDeep} />
                    <span>
                        {status.status === "submitted"
                            ? "Demann kat ou an ap tann verifikasyon admin."
                            : "Peman an konfime — n ap tann kat la vin aktif. Sa ka pran kèk minit."}
                    </span>
                </div>
                <section className="sec">
                    <div className="list g g-card">
                        <div className="row">
                            <span className="row-ico g g-chip"><Icon name="receipt" size={18} color={C.teal} /></span>
                            <div className="row-mid"><div className="row-nm">Referans</div><div className="row-sub n">{status.nit}</div></div>
                        </div>
                        <div className="row">
                            <span className="row-ico g g-chip"><Icon name="wallet" size={18} color={C.teal} /></span>
                            <div className="row-mid"><div className="row-nm">Balans mande</div><div className="row-sub">{fmtUSD(status.requested_balance_usd)}</div></div>
                        </div>
                    </div>
                </section>
            </div>
        )
    }

    // ── Refund owed — admin already flagged this one for repayment ──────
    if (status.status === "refund_owed") {
        return (
            <div className="blocked">
                <Icon name="alert" size={16} color={C.warn} />
                <span>N ap ranbouse ou pou demann {status.nit}{status.admin_note ? ` — ${status.admin_note}` : "."}</span>
            </div>
        )
    }

    // ── Active (or frozen) — the real card ───────────────────────────────
    const frozen = status.status === "frozen"
    return (
        <>
            <div style={{ display: "flex", alignItems: "center", gap: S[3] }}>
                <span className="row-ico g g-chip"><Icon name="card" size={18} color={C.teal} /></span>
                <h1 className="hdr-t">Kat Mwen</h1>
            </div>
            <motion.div className={`kat-scene${frozen ? " frozen" : ""}`}
                initial={reduced ? false : { opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={T.slow}>
                <div className="kat-face kat-front g g-dark">
                    <div className="kat-top">
                        <Press className="ico-d" onClick={reveal} scale={0.9} disabled={revealing}
                               aria-label={show ? "Kache" : "Montre"}>
                            {revealing ? <span className="spin" /> : <Icon name={show ? "eye" : "eyeOff"} size={17} color={C.onDark} />}
                        </Press>
                        {frozen && <span className="kat-chip">Jele</span>}
                    </div>
                    <div className="kat-pan n">
                        {show && cardNumber ? String(cardNumber) : status.last4 ? `•••• •••• •••• ${status.last4}` : "•••• •••• •••• ••••"}
                    </div>
                    <div className="kat-bot">
                        <div className="kat-nm">{status.card_name.toUpperCase()}</div>
                        <div className="kat-meta">
                            <span><i>Ekspirasyon</i><b className="n">{show && expiry ? String(expiry) : "••/••"}</b></span>
                            <span><i>CVV</i><b className="n">{show && cvv ? String(cvv) : "•••"}</b></span>
                        </div>
                    </div>
                    <div className="kat-brand"><PayvitLogo size={20} /> Payvit</div>
                </div>
            </motion.div>
            <p className="kat-hint">{show ? "Kat la ap kache otomatikman apre 20 segond" : "Tape zye a pou montre nimewo a"}</p>

            <section className="sec">
                <h2 className="sec-t">Aksyon</h2>
                <div className="list g g-card">
                    <Press as="div" role="button" tabIndex={0} className="row" scale={0.99} onClick={() => setShowTopup((v) => !v)}>
                        <span className="row-ico g g-chip"><Icon name="plus" size={18} color={C.teal} /></span>
                        <div className="row-mid"><div className="row-nm">Rechaje kat la</div><div className="row-sub">Ajoute lajan sou kat vityèl la</div></div>
                        <Icon name={showTopup ? "chevronDown" : "chevronRight"} size={18} color={C.textMute} />
                    </Press>
                    <Press as="div" role="button" tabIndex={0} className="row" scale={0.99} onClick={toggleFreeze}>
                        <span className="row-ico g g-chip">{freezing ? <span className="spin" /> : <Snow />}</span>
                        <div className="row-mid"><div className="row-nm">{frozen ? "Dejele kat la" : "Jele kat la"}</div>
                            <div className="row-sub">Fonksyon sa a poko disponib pou kat sa yo</div></div>
                    </Press>
                </div>
            </section>

            {showTopup && (
                <CardRequestForm mode="topup" getAccessToken={getAccessToken} say={say}
                                  onSubmitted={() => { setShowTopup(false); refresh() }} />
            )}

            <section className="sec">
                <h2 className="sec-t">Sou kat sa a</h2>
                <div className="list g g-card">
                    {[
                        { i: "shield" as IconName, t: "Kat la pwoteje", s: "Sèl ou ka wè nimewo a. Li kache otomatikman apre 20 segond." },
                        { i: "wallet" as IconName, t: "Balans mande", s: fmtUSD(status.requested_balance_usd) },
                    ].map((r) => (
                        <div className="row" key={r.t}>
                            <span className="row-ico g g-chip"><Icon name={r.i} size={18} color={C.teal} /></span>
                            <div className="row-mid"><div className="row-nm">{r.t}</div><div className="row-sub">{r.s}</div></div>
                        </div>
                    ))}
                </div>
            </section>
        </>
    )
}

function Istorik({ loading, setDetail, money, highlight, txs }: any) {
    const [f, setF] = React.useState<"tout" | "antre" | "soti">("tout")
    const [q, setQ] = React.useState("")
    const list = (txs as Tx[]).filter((t) => {
        const dirOk = f === "tout" || (f === "antre" ? t.kind === "depo" : t.kind !== "depo")
        const qOk = !q.trim() || (t.name + t.method + t.nit).toLowerCase().includes(q.toLowerCase())
        return dirOk && qOk
    })
    return (
        <>
            <div className="field g g-card">
                <Icon name="search" size={18} color={C.textMute} />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Chèche non, metòd oswa NIT…" aria-label="Chèche" />
            </div>
            <div className="chips">
                {([["tout", "Tout"], ["antre", "Antre"], ["soti", "Soti"]] as const).map(([k, l]) => (
                    <Press key={k} className={`chip g g-chip${f === k ? " on" : ""}`} onClick={() => setF(k)} scale={0.94}>
                        {f === k && <motion.span layoutId="flt-pill" className="chip-fill" transition={SPRING} />}
                        <span style={{ position: "relative", zIndex: 1 }}>{l}</span>
                    </Press>
                ))}
            </div>
            <div className="list g g-card">
                {loading ? (
                    [0, 1, 2, 3, 4].map((i) => <RowSkeleton key={i} />)
                ) : list.length ? (
                    <AnimatePresence initial={false}>
                        {list.map((t, i) => (
                            <motion.div key={t.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                                        exit={{ opacity: 0, height: 0 }} transition={T.quick}>
                                <Row tx={t} i={i} money={money} onOpen={() => setDetail(t)} hi={highlight === t.id} />
                            </motion.div>
                        ))}
                    </AnimatePresence>
                ) : (
                    <div className="empty">
                        <Icon name={q.trim() ? "search" : "clock"} size={26} color={C.textMute} />
                        <div>{q.trim() ? `Nou pa jwenn anyen pou "${q}"` : "Ou pa gen tranzaksyon poko"}</div>
                    </div>
                )}
            </div>
        </>
    )
}

function Profil({ say, txs, topup, avatarPref, setAvatarPref, user: USER, onSignOut, balanceUsd, vitCount, rateDepo, depositsEnabled, transfersEnabled, balanceKnown, getAccessToken }: any) {
    const reduced = useReduced()
    const list: Tx[] = txs ?? []
    const pending = list.filter((t) => t.statut === "en_attente").length

    /** 'on'/'off' reflects whether THIS browser actually holds a push
     *  subscription right now — not a remembered preference, which could
     *  say "on" after the user cleared site data or uninstalled the PWA
     *  and the real subscription is long gone. */
    const [pushState, setPushState] = React.useState<"loading" | "unsupported" | "off" | "on" | "denied">("loading")
    const [pushBusy, setPushBusy] = React.useState(false)
    React.useEffect(() => {
        if (!isPushSupported()) { setPushState("unsupported"); return }
        if (Notification.permission === "denied") { setPushState("denied"); return }
        isCurrentlySubscribed().then((sub) => setPushState(sub ? "on" : "off"))
    }, [])

    const togglePush = async () => {
        if (pushBusy || pushState === "unsupported" || pushState === "denied") return
        setPushBusy(true)
        const token = await getAccessToken()
        if (!token) {
            setPushBusy(false)
            say("Sesyon ekspire — konekte ankò.")
            return
        }
        if (pushState === "on") {
            await disablePush(token)
            setPushState("off")
            say("Notifikasyon dezaktive")
        } else {
            const result = await enablePush(token)
            if (result === "enabled") { setPushState("on"); say("Notifikasyon aktive") }
            else if (result === "denied") { setPushState("denied"); say("Ou refize notifikasyon yo nan navigatè a.") }
            else if (result === "unsupported") setPushState("unsupported")
            else say("Nou pa t kapab aktive notifikasyon yo. Eseye ankò.")
        }
        setPushBusy(false)
    }
    /** The rate and the clock come from the SAME constants the converter and
     *  the dashboard read — not a second copy that can drift. */
    const [clock, setClock] = React.useState(() => new Date())
    React.useEffect(() => {
        const i = window.setInterval(() => setClock(new Date()), 1000)
        return () => window.clearInterval(i)
    }, [])
    const hhmmss = clock.toTimeString().slice(0, 8)

    const PREFS = [
        { k: "auto" as const, label: "Oto" },
        { k: "feminine" as const, label: "Fi" },
        { k: "masculine" as const, label: "Gason" },
        { k: "neutral" as const, label: "Net" },
    ]
    const shown = avatarPref === "auto" ? undefined : avatarPref

    return (
        <>
            <div className="pf-hd g g-card">
                <span className="pf-av">
                    <Avatar seed={USER.seed} name={USER.name} size={112} presentation={shown} ring />
                    <span className="pf-badge"><Icon name="check" size={13} color="#fff" stroke={3} /></span>
                </span>
                <div className="pf-n">{USER.email.split("@")[0]}</div>
                <div className="pf-e">{USER.email}</div>
                <span className="pf-verif">
                    <Icon name="shield" size={15} color={C.ok} /> Kont Verifye
                </span>
            </div>

            <div className="pf-grid">
                {[
                    { k: "Solde", v: balanceKnown ? fmtUSD(balanceUsd + (topup ?? 0)) : "—" },
                    { k: "VIT", v: balanceKnown ? String(vitCount) : "—" },
                    { k: "Tranzaksyon", v: String(list.length) },
                    { k: "An atant", v: String(pending) },
                ].map((c, i) => (
                    <motion.div key={c.k} className="pf-stat g g-card"
                        initial={reduced ? false : { opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ ...T.base, delay: reduced ? 0 : i * 0.05 }}>
                        <span className="lbl">{c.k}</span>
                        <div className="pf-statv">{c.v}</div>
                    </motion.div>
                ))}
            </div>

            <div className="pf-card g g-card">
                <span className="lbl">Estati sèvis</span>
                <div className="pf-svc">
                    <span className="pf-pill" data-off={depositsEnabled === false}>
                        <i className="pf-dot" /> {depositsEnabled === false ? "Depo Endisponib" : "Depo Disponib"}
                    </span>
                    <span className="pf-pill" data-off={transfersEnabled === false}>
                        <i className="pf-dot" /> {transfersEnabled === false ? "Transfè Endisponib" : "Transfè Disponib"}
                    </span>
                </div>
                <div className="pf-div" />
                <div className="pf-row"><span>To chanj</span><span className="n">1 USD = {rateDepo} HTG</span></div>
                <div className="pf-row"><span>Dènye mizajou</span><span className="n">{hhmmss}</span></div>
            </div>

            <div className="pf-card g g-card">
                <span className="lbl">Avatar mwen</span>
                <p className="pf-note">Otomatik chwazi dapre non ou. Chwa ou a toujou genyen.</p>
                <div className="pf-avrow">
                    <Avatar seed={USER.seed} name={USER.name} size={72} presentation={shown} />
                    <div className="seg g g-chip pf-seg">
                        {PREFS.map((o) => (
                            <Press key={o.k} className={`seg-b${avatarPref === o.k ? " on" : ""}`}
                                   onClick={() => setAvatarPref(o.k)} scale={0.94}>
                                {avatarPref === o.k && (
                                    <motion.span layoutId="pf-pill" className="seg-fill" transition={SPRING} />
                                )}
                                <span>{o.label}</span>
                            </Press>
                        ))}
                    </div>
                </div>
            </div>

            <div className="pf-card g g-card">
                <span className="lbl">Notifikasyon</span>
                <p className="pf-note">
                    {pushState === "unsupported"
                        ? "Navigatè sa a pa sipòte notifikasyon push."
                        : pushState === "denied"
                          ? "Ou bloke notifikasyon yo nan paramèt navigatè a — ale nan paramèt yo pou aktive l ankò."
                          : "Resevwa yon notifikasyon dirèk sou aparèy ou lè yon tranzaksyon chanje estati."}
                </p>
                {(pushState === "on" || pushState === "off") && (
                    <Press className="cta" disabled={pushBusy} onClick={togglePush} style={{ marginTop: S[3] }}>
                        {pushBusy
                            ? <span className="spin" />
                            : pushState === "on" ? "Dezaktive Notifikasyon" : "Aktive Notifikasyon"}
                    </Press>
                )}
            </div>

            <Press as="div" role="button" tabIndex={0} className="pf-act g g-card"
                   onClick={() => say("Done yo rafrechi")} scale={0.99}>
                <Icon name="swap" size={19} color={C.teal} stroke={1.9} /> Rafrechi done yo
            </Press>
            <Press as="div" role="button" tabIndex={0} className="pf-act pf-danger g g-card"
                   onClick={onSignOut} scale={0.99}>
                <Icon name="arrowRight" size={19} color={C.danger} stroke={1.9} /> Dekonekte
            </Press>
        </>
    )
}

/** Detail sheet. The avatar shares `layoutId` with the row that opened it, so
 *  the sheet grows out of the row instead of appearing over it. */
// ═══════════════════════════════════════════════════════════════════════════
// TRACKER — "Kote lajan an ye"
//
// PORTED from framer/PayvitDashboard.tsx (deriveTracker / STATE_TONE /
// TrackerInline / TrackerTimeline, ~line 2540-2995). Same four stages in the
// same order, the same mapping from `statut` to a position in that order, the
// same five tones, the same icon per tone and the same pulse.
//
// Per-stage times come from `tx.feed.stage_history` — the fixture stands in
// for the live feed and has the same shape. A stage with no entry renders
// WITHOUT a time rather than with a guess; on a money movement an invented
// timestamp is worse than an honest blank.
//
// ONE component, used in three places: the pill on a transaction row, the pill
// in the sheet header, and the full timeline in the sheet. There is no
// simplified variant — a second copy is how the two drift apart.
// ═══════════════════════════════════════════════════════════════════════════
type StageState = "done" | "active" | "pending" | "failed" | "delayed"
const STAGE_ORDER: StageKey[] = ["resevwa", "ap_trete", "voye", "rive"]

const stageLabel = (k: StageKey, method: string, kind: string): string =>
    k === "resevwa" ? "Resevwa"
    : k === "ap_trete" ? "Ap trete"
    : k === "voye" ? `Voye bay ${method || "operatè a"}`
    : kind === "depo" ? "Kredite" : "Rive"

const STAGE_TONE: Record<StageState, { c: string; bg: string; icon: IconName; word: string }> = {
    done: { c: C.ok, bg: C.okSoft, icon: "circleCheck", word: "Fini" },
    active: { c: C.teal, bg: "rgba(26,157,139,.13)", icon: "loader", word: "Ap fèt" },
    pending: { c: C.textMute, bg: "rgba(78,122,117,.10)", icon: "circleDashed", word: "Ap tann" },
    delayed: { c: C.warn, bg: C.warnSoft, icon: "hourglass", word: "An reta" },
    failed: { c: C.danger, bg: C.dangerSoft, icon: "circleX", word: "Bloke" },
}

function deriveTracker(tx: Tx) {
    const settled = tx.statut === "paye" || tx.statut === "complété"
    const failed = tx.statut === "rejete"
    const approved = tx.statut === "approuve"
    const feed = tx.feed

    /** Where the money actually is. The feed's own history is the authority
     *  when we have it — the furthest stage it has stamped is the last thing
     *  that definitely happened, so the one after it is what is in progress.
     *  `statut` is the fallback. */
    const stamped = feed?.stage_history?.length ?? 0
    const currentIndex = settled ? 3
        : stamped > 0 ? Math.min(stamped, 3)
        : approved ? 2 : 1

    /** Real transactions carry no per-stage feed at all — the backend
     *  returns one overall status, never stage-by-stage timing, so
     *  `feed`/`stage_history` only ever exist on the old fixture rows.
     *  The one genuinely real timestamp available for a real transaction
     *  is its own `tx.at` — accurate for whichever stage the transaction
     *  is ACTUALLY at right now (the last one, once settled), so that's
     *  the only stage it gets attached to. Every other stage with nothing
     *  real to show stays honestly blank rather than inventing a time. */
    const at = (k: StageKey, i: number) => {
        const stamped = feed?.stage_history?.find((h) => h.stage === k)?.at
        if (stamped) return stamped
        if (settled && i === STAGE_ORDER.length - 1) return tx.at
        return undefined
    }

    const stages = STAGE_ORDER.map((key, i) => {
        let state: StageState =
            i < currentIndex ? "done" : i === currentIndex ? "active" : "pending"
        if (settled) state = "done"
        if (failed && i === currentIndex) state = "failed"
        if (failed && i > currentIndex) state = "pending"
        if (feed?.delayed && i === currentIndex && !settled && !failed) state = "delayed"
        return {
            key, state,
            label: stageLabel(key, tx.method, tx.kind),
            at: at(key, i),
            /** Only ever on the stage in progress. */
            eta: i === currentIndex && !settled && !failed ? feed?.eta_next : undefined,
        }
    })

    const inFlight = !settled && !failed
    return {
        stages, currentIndex, settled, failed, inFlight,
        delayed: !!feed?.delayed && inFlight,
        currentLabel: stages[Math.min(currentIndex, 3)].label,
        reason: feed?.reason,
        nextStep: feed?.next_step,
    }
}
type TrackerView = ReturnType<typeof deriveTracker>

/** Compact form — the row pill and the sheet header. Where it is now, and how
 *  far along. State is carried by colour AND words AND the filled segments,
 *  never colour alone. */
function TrackerPill({ view, bars = true }: { view: TrackerView; bars?: boolean }) {
    const reduced = useReduced()
    const tone = STAGE_TONE[
        view.failed ? "failed" : view.delayed ? "delayed" : view.inFlight ? "active" : "done"
    ]
    return (
        <span className="trk-pill" style={{ background: tone.bg, color: tone.c,
                                            borderColor: `${tone.c}22` }}>
            <span className={`trk-dot${view.inFlight && !reduced ? " beat" : ""}`}
                  style={{ background: tone.c }} />
            {view.failed ? "Bloke" : view.currentLabel}
            {bars && (
                <span className="trk-bars" aria-hidden>
                    {view.stages.map((s, i) => (
                        <motion.span
                            key={s.key} className="trk-bar"
                            style={{ background: s.state === "pending" ? `${tone.c}33` : tone.c }}
                            /* The segments fill left to right as the money
                               moves, one after the other rather than all at
                               once — the stagger is what makes it read as
                               progress instead of decoration. */
                            initial={reduced ? false : { scaleX: 0, opacity: 0 }}
                            animate={{ scaleX: 1, opacity: s.state === "active" ? 0.75 : 1 }}
                            transition={reduced ? { duration: 0 }
                                : { ...T.base, delay: 0.06 * i }}
                        />
                    ))}
                </span>
            )}
        </span>
    )
}

/** Full timeline — the transaction sheet. */
function Tracker({ tx, onSupport }: { tx: Tx; onSupport: () => void }) {
    const reduced = useReduced()
    const view = deriveTracker(tx)
    return (
        <>
            <div className="trk-hd">
                <span className="lbl">Kote lajan an ye</span>
                <TrackerPill view={view} />
            </div>
            <div className="trk g g-card">
                {view.stages.map((st, i) => {
                    const tone = STAGE_TONE[st.state]
                    const last = i === view.stages.length - 1
                    return (
                        <div className="trk-row" key={st.key}>
                            <span className="trk-mark">
                                <span
                                    className={`trk-node${st.state === "active" && !reduced ? " beat" : ""}`}
                                    style={{ background: tone.bg,
                                             borderColor: `${tone.c}${st.state === "pending" ? "33" : "66"}` }}
                                >
                                    {st.state === "done" && !reduced ? (
                                        /* Drawn, not popped. The check strokes
                                           itself on so a completed stage reads
                                           as something that just finished. */
                                        <svg width={16} height={16} viewBox="0 0 24 24" fill="none"
                                             stroke={tone.c} strokeWidth={2}
                                             strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="9" />
                                            <motion.path
                                                d="M8.4 12.2l2.5 2.5 4.7-4.9"
                                                initial={{ pathLength: 0 }}
                                                animate={{ pathLength: 1 }}
                                                transition={{ duration: 0.34, ease: EASE, delay: 0.1 + i * 0.08 }}
                                            />
                                        </svg>
                                    ) : (
                                        <Icon name={tone.icon} size={16} color={tone.c} stroke={2} />
                                    )}
                                </span>
                                {!last && (
                                    <span className="trk-line"
                                          style={{ background: st.state === "done" ? C.ok : C.hairline }} />
                                )}
                            </span>
                            <span className="trk-mid">
                                <span className="trk-l" style={{ color: st.state === "pending" ? C.textMute : C.text }}>
                                    {st.label}
                                </span>
                                {/* A real time when we have one, an estimate
                                    while it's in progress, otherwise a plain
                                    dash — never a guess, and never a sentence
                                    repeated on every stage that has nothing
                                    real to show. */}
                                <span className="trk-at">
                                    {st.at ? st.at : st.eta ? `Estimasyon: ${st.eta}` : "—"}
                                </span>
                            </span>
                            <span className="trk-word" style={{ color: tone.c }}>{tone.word}</span>
                        </div>
                    )
                })}

                {(view.failed || view.delayed) && (
                    <div className="trk-note" style={{
                        background: view.failed ? C.dangerSoft : C.warnSoft,
                        borderColor: view.failed ? "rgba(196,68,61,.22)" : "rgba(180,112,14,.24)",
                    }}>
                        <Icon name={view.failed ? "circleX" : "hourglass"} size={18}
                              color={view.failed ? C.danger : C.warn} />
                        <span>
                            <strong style={{ color: view.failed ? C.danger : C.warn }}>
                                {view.failed ? "Tranzaksyon an pa t pase" : "Sa ap pran plis tan pase nòmal"}
                            </strong>
                            {view.reason ?? (view.failed
                                ? "Lajan an pa kite kont ou. Si yo te debite w, ekip la ap remèt li."
                                : "Nou poko gen yon nouvo estimasyon pou etap sa.")}
                            {view.nextStep ? ` ${view.nextStep}` : ""}
                        </span>
                    </div>
                )}

                {/* Support is offered while the money is still moving or stuck.
                    A settled transfer has nothing to chase, so the row is
                    absent rather than present-and-pointless. */}
                {(view.failed || view.inFlight) && (
                    <Press className="trk-help" onClick={onSupport}>
                        <Icon name="message" size={18} color={C.tealDeep} />
                        {view.failed ? "Kontakte sipò" : "Yon pwoblèm? Ekri sipò"}
                    </Press>
                )}
            </div>
        </>
    )
}


function TxSheet({ tx, onClose, money, say, user }: any) {
    const reduced = useReduced()
    const { copied, copy } = useCopy(say)
    const copyNit = (nit: string) => copy(nit, "Referans kopye")
    const [sharing, setSharing] = React.useState(false)
    const [downloading, setDownloading] = React.useState(false)
    const [downloadingPdf, setDownloadingPdf] = React.useState(false)

    const onShare = async () => {
        if (sharing || !tx) return
        setSharing(true)
        const outcome = await shareReceipt(tx, user)
        setSharing(false)
        if (outcome === "shared") say("Resi a pataje")
        else if (outcome === "copied") say("Resi a kopye — kole l kèlkeswa kote ou vle pataje l")
        else if (outcome === "failed") say("Nou pa t kapab pataje resi a")
        // "cancelled": user dismissed the OS share sheet — no message needed.
    }
    const onDownload = async () => {
        if (downloading || !tx) return
        setDownloading(true)
        const ok = await downloadReceipt(tx, user)
        setDownloading(false)
        say(ok ? "Resi a telechaje" : "Nou pa t kapab telechaje resi a")
    }
    const onDownloadPdf = async () => {
        if (downloadingPdf || !tx) return
        setDownloadingPdf(true)
        const ok = await downloadReceiptPdf(tx, user)
        setDownloadingPdf(false)
        say(ok ? "Resi PDF telechaje" : "Nou pa t kapab telechaje PDF la")
    }
    React.useEffect(() => {
        if (!tx) return
        const k = (e: KeyboardEvent) => e.key === "Escape" && onClose()
        window.addEventListener("keydown", k)
        document.body.style.overflow = "hidden"
        return () => { window.removeEventListener("keydown", k); document.body.style.overflow = "" }
    }, [tx, onClose])
    return (
        <AnimatePresence>
            {tx && (
                <motion.div className="scrim" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            transition={T.quick} onClick={onClose} role="dialog" aria-modal="true">
                    <motion.div className="sheet g g-sheet" onClick={(e: any) => e.stopPropagation()}
                        initial={reduced ? { opacity: 0 } : { y: "100%" }} animate={reduced ? { opacity: 1 } : { y: 0 }}
                        exit={reduced ? { opacity: 0 } : { y: "100%" }} transition={SPRING}>
                        <span className="grab" />
                        {/* Who, how much, what state — one card. Loose on the
                            sheet background these three read as three separate
                            things; boxed, they read as one transaction, and the
                            sheet becomes a stack of matching cards down to the
                            tracker and the reference block. The close control
                            stays outside it: it acts on the sheet, not on the
                            transaction. */}
                        <div className="sheet-card g g-card">
                            <div className="sheet-hd">
                                <motion.span layoutId={`av-${tx.id}`} transition={SPRING}>
                                    <Avatar seed={tx.seed} name={tx.name} size={52} />
                                </motion.span>
                                <div style={{ minWidth: 0 }}>
                                    <div className="sheet-nm">{tx.name}</div>
                                    <div className="sheet-sub">{tx.handle}</div>
                                </div>
                                <Press className="ico g g-chip" onClick={onClose} aria-label="Fèmen">
                                    <Icon name="x" size={18} color={C.text} />
                                </Press>
                            </div>
                            <div className={`sheet-amt${tx.kind !== "depo" ? " out" : ""}`}>
                                {tx.kind !== "depo" ? "−" : "+"}{money(tx.amount)}
                            </div>
                            <span className={`pill pill-${STATUS[tx.statut].tone}`} style={{ alignSelf: "center" }}>
                                {STATUS[tx.statut].label}
                            </span>
                        </div>
                        <Tracker tx={tx} onSupport={() => say("N ap konekte w ak sipò")} />

                        {/* Reference block, in the dashboard's own order:
                            identity first (what to quote to support), then the
                            money. The rule separates the two — above it is
                            what the transaction IS, below it is what it cost. */}
                        <div className="rev-rows g g-card">
                            <Press as="div" role="button" tabIndex={0} className="rev-row rev-copy"
                                   scale={0.99} onClick={() => copyNit(tx.nit)}>
                                <span className="rev-k">Referans (NIT)</span>
                                <span className="rev-v n">
                                    {tx.nit}
                                    <Icon name={copied ? "check" : "copy"} size={14}
                                          color={copied ? C.ok : C.textMute} stroke={2.2} />
                                </span>
                            </Press>
                            <RevRow k="Metòd" v={tx.method} />
                            <RevRow k="Tip" v={tx.kind === "depo" ? "Depo" : tx.kind === "retre" ? "Retrè" : "Transfè"} />
                            <RevRow k="Estati" v={STATUS[tx.statut].label} />
                            {tx.note && <RevRow k="Nòt" v={tx.note} />}
                            <RevRow k="Dat" v={tx.at} />
                            <div className="rev-div" />
                            <RevRow k="Montan" v={fmtUSD(tx.amount)} big />
                            {/* Rate/fee are only real for this-session
                                submissions and a handful of history rows —
                                the backend has never persisted a
                                point-in-time rate, so a transaction without
                                one shows nothing here rather than today's
                                rate passed off as history. */}
                            {tx.rate != null && <RevRow k="Ekivalan" v={fmtHTG(tx.amount * tx.rate)} />}
                            {tx.fee != null && <RevRow k="Frè" v={fmtUSD(tx.fee)} />}
                            {tx.rate != null && <RevRow k="To" v={`1 USD = ${tx.rate} HTG`} />}
                        </div>

                        {/* Both routes offered. Sharing is three taps; saving
                            is the user's own business — neither substitutes
                            for the other. */}
                        <div className="rcp">
                            <Press className="cta" disabled={sharing} onClick={onShare}>
                                {sharing ? <span className="spin" /> : <Icon name="share" size={18} color="#fff" stroke={1.9} />} Pataje resi
                            </Press>
                            <Press className="cta cta-ghost" disabled={downloading} onClick={onDownload}>
                                {downloading ? <span className="spin" /> : <Icon name="download" size={18} color={C.tealDeep} stroke={1.9} />} Telechaje resi
                            </Press>
                            <Press as="div" role="button" tabIndex={0} className="hint"
                                   style={{ textAlign: "center", cursor: "pointer", color: C.teal, fontWeight: 700 }}
                                   onClick={onDownloadPdf}>
                                {downloadingPdf ? <><span className="spin" /> N ap prepare PDF…</> : "Telechaje kòm PDF"}
                            </Press>
                            <Press className="cta cta-plain" onClick={onClose}>Fèmen</Press>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}

/** The field asked for depends entirely on `provider`: a rail wants a phone
 *  number, an international wallet wants that wallet's own tag — never a
 *  hardcoded "Cashtag" regardless of what's actually selected. Inputs are
 *  controlled and actually collected now — previously this sheet threw away
 *  whatever was typed and `onSave` carried no data at all. */
function RecipSheet({ open, onClose, onSave, provider }: {
    open: boolean; onClose: () => void
    onSave: (draft: { provider: Provider; label: string; handle: string }) => Promise<boolean>
    provider: Provider
}) {
    const reduced = useReduced()
    /** Three distinct shapes, not two — a phone-based rail, an
     *  international wallet tag, and "Payvit" itself, which the backend
     *  identifies by EMAIL (`receiver_payvit_email`), not a phone number.
     *  Lumping Payvit in with MonCash/NatCash here was a real bug: it
     *  would have collected a phone number the backend never asked for
     *  and silently dropped the field it actually requires. */
    const isPhoneRail = provider === "MonCash" || provider === "NatCash"
    const isPayvitEmail = provider === "Payvit"
    const [name, setName] = React.useState("")
    const [tagOrPhone, setTagOrPhone] = React.useState("")

    React.useEffect(() => {
        if (open) { setName(""); setTagOrPhone("") }
    }, [open, provider])

    const fieldLabel = isPhoneRail ? "Nimewo benefisyè a" : isPayvitEmail ? "Imèl kont Payvit la" : TAG_LABEL[provider as Intl]
    const fieldPlaceholder = isPhoneRail ? "+509 38 12 3456" : isPayvitEmail ? "imel@egzanp.com" : TAG_PLACEHOLDER[provider as Intl]
    const emailValid = !isPayvitEmail || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(tagOrPhone)
    const canSave = name.trim().length > 0 && tagOrPhone.trim().length > 0 && emailValid
    const [saving, setSaving] = React.useState(false)

    const save = async () => {
        if (!canSave || saving) return
        setSaving(true)
        // On failure onSave returns false and the sheet stays open with
        // whatever was typed still in it — say() already told the user why,
        // retyping the same name/handle to try again would be needless.
        await onSave({ provider, label: name.trim(), handle: tagOrPhone.trim() })
        setSaving(false)
    }

    return (
        <AnimatePresence>
            {open && (
                <motion.div className="scrim" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            transition={T.quick} onClick={onClose} role="dialog" aria-modal="true">
                    <motion.div className="sheet g g-sheet" onClick={(e: any) => e.stopPropagation()}
                        initial={reduced ? { opacity: 0 } : { y: "100%" }} animate={reduced ? { opacity: 1 } : { y: 0 }}
                        exit={reduced ? { opacity: 0 } : { y: "100%" }} transition={SPRING}>
                        <span className="grab" />
                        <div className="sheet-hd">
                            <div style={{ minWidth: 0 }}>
                                <div className="sheet-nm">Nouvo resipyan</div>
                                <div className="sheet-sub">{provider}</div>
                            </div>
                            <Press className="ico g g-chip" onClick={onClose} aria-label="Fèmen"><Icon name="x" size={18} color={C.text} /></Press>
                        </div>
                        <label className="fld">
                            <span className="lbl">Non resipyan an</span>
                            <div className="field g g-chip">
                                <input value={name} onChange={(e) => setName(e.target.value)}
                                       placeholder="Non konplè" aria-label="Non resipyan an" />
                            </div>
                        </label>
                        <label className="fld">
                            <span className="lbl">{fieldLabel}</span>
                            <div className="field g g-chip">
                                <input value={tagOrPhone} onChange={(e) => setTagOrPhone(e.target.value)}
                                       type={isPayvitEmail ? "email" : "text"}
                                       inputMode={isPhoneRail ? "tel" : isPayvitEmail ? "email" : "text"}
                                       placeholder={fieldPlaceholder}
                                       aria-label={fieldLabel} />
                            </div>
                        </label>
                        {isPayvitEmail && tagOrPhone && !emailValid && (
                            <div className="vfy-err">Imèl sa a pa valid.</div>
                        )}
                        <Press className="cta" onClick={save} disabled={!canSave || saving}>
                            {saving ? <><span className="spin" /> N ap sove...</> : "Sove resipyan an"}
                        </Press>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}

// ── Brand bits ─────────────────────────────────────────────────────────────
function Snow() {
    return (
        <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7}
             strokeLinecap="round" aria-hidden>
            <path d="M12 2v20" /><path d="m20.7 7-17.4 10" /><path d="m3.3 7 17.4 10" />
            <path d="m9 4 3 3 3-3" /><path d="m9 20 3-3 3 3" />
        </svg>
    )
}
function Brand({ p }: { p: string }) {
    return <span className="brand-dot" style={{ background: p === "MonCash" ? "#E4322B" : p === "NatCash" ? "#1B7F3B" : C.teal }} />
}

// ═══════════════════════════════════════════════════════════════════════════
// INTRO — the threshold. Logo, one door, nothing else.
//
// The wordmark and the strapline are gone: the mark already says the name, and
// a sentence nobody reads is not confidence. What is left has to be placed,
// not merely centred — hence the fixed optical block rather than a stack that
// drifts with the viewport.
// ═══════════════════════════════════════════════════════════════════════════

/** How long the confirm sequence runs before the app opens. */
const INTRO_CONFIRM_MS = 1800

/** Geometry, declared once. Every position in the sequence is derived from
 *  these, so the knob's rest, its travel and its circle can never disagree. */
const IC = { w: 300, h: 64, pad: 7 }
const IC_KNOB_W = 200
const IC_KNOB_H = IC.h - IC.pad * 2          // 50
const IC_TRAVEL = IC.w - IC.pad * 2 - IC_KNOB_W  // 86 — the swipe distance
/** Two thirds of the way is a commitment; the last third is friction. */
const IC_COMMIT = IC_TRAVEL * 0.62
/** Where the knob sits once it is a circle: dead centre of the track. */
const IC_CENTRE_X = (IC.w - IC_KNOB_H) / 2 - IC.pad

/** The CTA: a slider that is also a button, and a shape that becomes a circle.
 *
 *  THE MORPH IS ONE ELEMENT. The knob is absolutely positioned inside the
 *  track, so animating its own width and height moves nothing else on the
 *  page — no sibling reflows, nothing below it shifts. Because its radius is
 *  already `999px` (clamped by the browser to half the shorter side), the
 *  corners round themselves as the width closes: a 200×50 pill contracting to
 *  50×50 IS a circle at the end of the tween, with every frame in between a
 *  correctly-rounded intermediate. No second element scales in behind it.
 *
 *  Two ways in, one outcome:
 *    · drag the knob past ~62% of the track
 *    · tap it
 *  Both call `confirm()`. A drag that moved is not also a click — `moved`
 *  guards that, because framer-motion still fires the button's onClick after
 *  a pointer drag ends on it. */
function IntroCta({ onDone }: { onDone: () => void }) {
    const reduced = useReduced()
    const [go, setGo] = React.useState(false)
    const x = useMotionValue(0)
    const moved = React.useRef(false)

    const confirm = React.useCallback(() => setGo((g) => g || true), [])

    React.useEffect(() => {
        if (!go) return
        const t = window.setTimeout(onDone, reduced ? 900 : INTRO_CONFIRM_MS)
        return () => window.clearTimeout(t)
    }, [go, reduced, onDone])

    /* The lane dims as the knob covers it, so the hint recedes exactly as the
       gesture makes it redundant. */
    const laneOpacity = useTransform(x, [0, IC_TRAVEL], [1, 0])

    return (
        <div className="ic" data-go={go}
             style={{ width: IC.w, height: IC.h, padding: IC.pad }}>
            {/* ── The track: frosted glass, not a fill ──────────────────── */}
            <motion.div className="ic-track g"
                animate={{ opacity: go ? 0 : 1 }}
                transition={{ duration: reduced ? 0.2 : 0.44, ease: EASE, delay: go && !reduced ? 0.24 : 0 }}
                aria-hidden />

            {/* ── The affordance: chevrons drifting the way you should go ── */}
            <motion.span className="ic-lane" aria-hidden
                style={{ opacity: reduced ? 1 : laneOpacity, left: IC.pad + IC_KNOB_W, right: IC.pad }}>
                {[0, 1, 2].map((i) => (
                    <motion.span key={i} className="ic-chev"
                        animate={reduced || go ? { opacity: 0.35 } : { opacity: [0.15, 0.75, 0.15], x: [0, 3, 0] }}
                        transition={reduced || go ? { duration: 0.2 } : {
                            duration: 1.6, repeat: Infinity, ease: "easeInOut", delay: i * 0.18,
                        }}>
                        <Icon name="chevronRight" size={16} color={C.tealDeep} stroke={2.4} />
                    </motion.span>
                ))}
            </motion.span>

            {/* ── The knob: draggable, tappable, and the thing that morphs ── */}
            <motion.button
                type="button"
                className="ic-knob"
                aria-label="Kòmanse — glise oswa tape"
                disabled={go}
                style={{ x, top: IC.pad, left: IC.pad, borderRadius: 999 }}
                drag={go || reduced ? false : "x"}
                dragConstraints={{ left: 0, right: IC_TRAVEL }}
                dragElastic={0.04}
                dragMomentum={false}
                onDragStart={() => { moved.current = true }}
                onDragEnd={(_e, info) => {
                    const past = x.get() > IC_COMMIT || info.velocity.x > 700
                    if (past) confirm()
                    else animate(x, 0, SPRING)
                    // Let the click that follows the pointer-up pass by first.
                    window.setTimeout(() => { moved.current = false }, 60)
                }}
                onClick={() => { if (!moved.current) confirm() }}
                whileTap={reduced || go ? undefined : { scale: 0.985 }}
                initial={false}
                animate={
                    go
                        ? {
                              width: IC_KNOB_H,
                              height: IC_KNOB_H,
                              x: IC_CENTRE_X,
                              ...(reduced ? {} : {}),
                          }
                        : { width: IC_KNOB_W, height: IC_KNOB_H }
                }
                transition={
                    go
                        ? reduced
                            ? { duration: 0.2 }
                            : { duration: 0.62, ease: [0.65, 0, 0.2, 1] }
                        : SPRING
                }
            >
                <motion.span className="ic-label"
                    animate={{ opacity: go ? 0 : 1 }}
                    transition={{ duration: reduced ? 0.15 : 0.2, ease: EASE }}>
                    Kòmanse
                </motion.span>

                {/* The tick lives INSIDE the knob — it is the same object, so
                    there is nothing to cross-fade with. */}
                <span className="ic-tick" aria-hidden>
                    {/* FINITE, deliberately. An infinite animation inside an
                        AnimatePresence child never reports complete, so the
                        exit runs to opacity 0 and then the node is never
                        unmounted — an invisible full-screen layer with
                        pointer-events left on, swallowing every tap on the
                        dashboard behind it. One pulse lands at 1.7s, clear of
                        the 1.8s dismissal. */}
                    {go && !reduced && (
                        <motion.span className="ic-ring"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: [0.9, 1.55], opacity: [0.4, 0] }}
                            transition={{
                                duration: 0.65, ease: "easeOut", delay: 1.05,
                            }} />
                    )}
                    <svg width={26} height={26} viewBox="0 0 24 24" fill="none">
                        <motion.path
                            d="M20 6 9 17l-5-5"
                            stroke="#fff" strokeWidth={2.8}
                            strokeLinecap="round" strokeLinejoin="round"
                            initial={{ pathLength: 0, opacity: 0 }}
                            animate={{ pathLength: go ? 1 : 0, opacity: go ? 1 : 0 }}
                            transition={
                                go && !reduced
                                    ? {
                                          pathLength: { duration: 0.44, ease: EASE, delay: 0.58 },
                                          opacity: { duration: 0.01, delay: 0.58 },
                                      }
                                    : { duration: 0 }
                            }
                        />
                    </svg>
                </span>
            </motion.button>

            <span className="sr-live" role="status" aria-live="polite">
                {go ? "Konfime — n ap louvri Payvit" : ""}
            </span>
        </div>
    )
}

function Intro({ onStart, onLeaveStart }: { onStart: () => void; onLeaveStart: () => void }) {
    const reduced = useReduced()
    /** The screen owns its own departure.
     *
     *  AnimatePresence ran the exit to opacity 0 and then never removed the
     *  node — a full-screen layer with pointer-events still on, invisible and
     *  swallowing every tap on the dashboard behind it. Fading here and
     *  calling `onStart` on completion makes the unmount ordinary React state
     *  with no presence bookkeeping to stall. `pointerEvents` drops the moment
     *  the fade starts, so even a stalled frame cannot block input. */
    const [leaving, setLeaving] = React.useState(false)
    return (
        <motion.div
            className="intro"
            initial={{ opacity: 0 }}
            animate={
                leaving
                    ? reduced ? { opacity: 0 } : { opacity: 0, scale: 1.03 }
                    : { opacity: 1, scale: 1 }
            }
            transition={T.slow}
            onAnimationComplete={() => { if (leaving) onStart() }}
            style={{ pointerEvents: leaving ? "none" : "auto" }}
            role="dialog"
            aria-modal="true"
            aria-label="Byenveni sou Payvit"
        >
            {/* Same wash as the dashboard, same tokens — masked so the top and
                bottom edges resolve to flat SITE_BG with no seam. */}
            <div className="intro-wash" aria-hidden />
            <motion.div className="intro-body"
                initial={reduced ? false : { opacity: 0, y: 14, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ ...T.slow, delay: reduced ? 0 : 0.1 }}>
                <PayvitLogo size={132} />
            </motion.div>
            <motion.div className="intro-foot"
                initial={reduced ? false : { opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...T.slow, delay: reduced ? 0 : 0.22 }}>
                <IntroCta onDone={() => { setLeaving(true); onLeaveStart() }} />
            </motion.div>
        </motion.div>
    )
}

// ═══════════════════════════════════════════════════════════════════════════
// PROCESSING → SUCCESS
//
// One popup, two stages, cross-faded. Compact and centred rather than a
// full-screen takeover, so the transaction still reads as something that
// happened inside the app rather than a place the app went.
// ═══════════════════════════════════════════════════════════════════════════
const PROCESS_MS = 8000

function ProcessSheet({ tx, onFollow }: { tx: Tx | null; onFollow: (t: Tx) => void }) {
    const reduced = useReduced()
    const [done, setDone] = React.useState(false)
    React.useEffect(() => {
        if (!tx) { setDone(false); return }
        setDone(false)
        const t = window.setTimeout(() => setDone(true), PROCESS_MS)
        document.body.style.overflow = "hidden"
        return () => { window.clearTimeout(t); document.body.style.overflow = "" }
    }, [tx])
    return (
        <AnimatePresence>
            {tx && (
                <motion.div className="scrim proc-scrim" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }} transition={T.quick} role="dialog" aria-modal="true">
                    <motion.div className="proc g g-sheet"
                        initial={reduced ? { opacity: 0 } : { opacity: 0, y: 20, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.97 }}
                        transition={SPRING}>
                        {/* mode="wait" is safe here: this popup owns its whole
                            subtree and is never unmounted by a parent
                            transition mid-flight. */}
                        <AnimatePresence mode="wait" initial={false}>
                            {!done ? (
                                <motion.div key="run" className="proc-in"
                                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                    transition={T.base}>
                                    <PlaneMark reduced={reduced} />
                                    <h2 className="proc-t" aria-live="polite">Ap trete.</h2>
                                    <p className="proc-p">N ap voye demann ou an kounye a.</p>
                                </motion.div>
                            ) : (
                                <motion.div key="ok" className="proc-in"
                                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                    transition={T.base}>
                                    {/* Same ring treatment as the flow's own
                                        success screen — reused, not redrawn. */}
                                    <motion.span className="succ-ring"
                                        initial={reduced ? false : { scale: 0.6, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }} transition={SPRING}>
                                        <Icon name="check" size={30} color="#fff" stroke={2.8} />
                                    </motion.span>
                                    <h2 className="proc-t" aria-live="polite">Demann lan ale.</h2>
                                    <p className="proc-p">Ou ka swiv li depi sou tablodbò w la.</p>
                                    <Press className="cta proc-cta" onClick={() => onFollow(tx)}>
                                        Swiv Tranzaksyon <Icon name="arrowRight" size={17} color="#fff" stroke={2.2} />
                                    </Press>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}

/** A paper plane that keeps leaving. Restrained on purpose — a spinner reads
 *  as the app struggling; a send motion reads as the app working. */
function PlaneMark({ reduced }: { reduced: boolean }) {
    return (
        <span className="proc-plane">
            <motion.span
                animate={reduced ? {} : { x: [0, 7, 0], y: [0, -6, 0], opacity: [1, 0.55, 1] }}
                transition={reduced ? { duration: 0 } : { duration: 1.9, ease: EASE, repeat: Infinity }}
                style={{ display: "flex" }}
            >
                <Icon name="send" size={30} color={C.teal} stroke={1.6} />
            </motion.span>
            <span className="proc-trail" aria-hidden />
        </span>
    )
}

// ═══════════════════════════════════════════════════════════════════════════
// 1 · RESUME AN ABANDONED TRANSACTION
// ═══════════════════════════════════════════════════════════════════════════
function DraftPrompt({ draft, onResume, onDiscard }: any) {
    const reduced = useReduced()
    return (
        <AnimatePresence>
            {draft && (
                <motion.div className="scrim proc-scrim" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }} transition={T.quick} role="dialog" aria-modal="true">
                    <motion.div className="proc g g-sheet"
                        initial={reduced ? { opacity: 0 } : { opacity: 0, y: 18, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0 }} transition={SPRING}>
                        <div className="proc-in">
                            <span className="proc-plane"><Icon name="receipt" size={26} color={C.teal} /></span>
                            <h2 className="proc-t">Ou gen yon tranzaksyon ou pa t fini</h2>
                            <p className="proc-p">
                                Nou sere sa w te kòmanse {agoKreyol(draft.savedAt)}. Kontinye l oswa efase l?
                            </p>
                            <div className="draft-rows">
                                <div className="rev-row"><span className="rev-k">Kantite</span>
                                    <span className="rev-v n">{draft.amount || "—"}</span></div>
                                <div className="rev-row"><span className="rev-k">Wout</span>
                                    <span className="rev-v">{draft.rail} → {draft.intl}</span></div>
                                <div className="rev-row"><span className="rev-k">Etap</span>
                                    <span className="rev-v">{draft.step >= 1 ? "Verifye" : "Detay"}</span></div>
                            </div>
                            <Press className="cta proc-cta" onClick={onResume}>Kontinye kote m te rete a</Press>
                            <Press className="cta cta-ghost draft-ghost" onClick={onDiscard}>Efase epi rekòmanse</Press>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}

// ═══════════════════════════════════════════════════════════════════════════
// 7 · RECHAJE BALANS — its own form, scoped to "destination = my balance"
// ═══════════════════════════════════════════════════════════════════════════
function Rechaje({ say, onSubmitted, userId, getAccessToken, rateDepo: RATE_DEPO, vitCount, user: USER }: {
    say: (m: string) => void; onSubmitted: (t: Tx) => void
    userId: string; getAccessToken: () => Promise<string | null>; rateDepo: number; vitCount: number
    user: { seed: string; name: string; email: string; phone: string | null }
}) {
    const reduced = useReduced()
    const [step, setStep] = React.useState(0)
    const [travel, setTravel] = React.useState(1)
    /** Rails only. A Payvit balance is funded from a Haitian wallet — the
     *  international wallets are a Depo/Retrè route, not a top-up one. */
    const [from, setFrom] = React.useState<Rail>("MonCash")
    const [htg, setHtg] = React.useState("5000")
    const [busy, setBusy] = React.useState(false)
    const [vitOn, setVitOn] = React.useState(false)
    const [vitMode, setVitMode] = React.useState<VitMode>("reduce")
    /** The same hook every other route gates on — not a Rechaje-specific copy
     *  of it, so "no exceptions for this transaction type" is structural
     *  rather than a rule someone has to remember to keep in sync. */
    const verify = useVerification(from)

    const typed = parseFloat(htg) || 0
    const usd = typed / RATE_DEPO
    const fee = calcFee(usd)
    const { spend: vitSpend, appliedUsd: vitSaveUsd, feeAfterUsd: feeAfter, boostUsd } =
        applyVit(vitOn, vitMode, fee, vitCount)
    /** Rechaje credits your OWN balance, so "add" lands there in USD. */
    const credited = usd + boostUsd
    const goStep = (n: number) => { setTravel(n >= step ? 1 : -1); setStep(n) }

    const reference = React.useMemo(() => refFor("RCH"), [])
    /** Rechaje's own row set — no recipient, and the destination is always the
     *  user's own balance. That is the whole difference from a transfer, and
     *  the only thing that differs from the other types' summaries. */
    const sumRows: SumRow[] = [
        { icon: "wallet", k: "Metòd", v: String(from) },
        { icon: "user", k: "Nimewo ou", v: USER.phone ?? "—" },
        { icon: "send", k: "Voye bay", v: PAYVIT_ACCOUNT.name },
        { icon: "card", k: "Sou nimewo",
          v: from === "NatCash" ? PAYVIT_ACCOUNT.natcash : PAYVIT_ACCOUNT.moncash },
        { icon: "receipt", k: "Prèv", v: verify.proof?.name ?? "—" },
        ...(verify.hasTxn ? [{ icon: "shield" as const, k: "Txn ID", v: verify.txn } as SumRow] : []),
        { icon: "download", k: "Ou voye", v: `${fmt(typed, 0)} HTG` },
        { icon: "wallet", k: "Frè Payvit", v: `${fmt(fee * RATE_DEPO, 0)} HTG` },
        { icon: "zap", k: "Aplike VIT",
          v: !vitOn || vitSpend < 1
              ? "Non"
              : vitMode === "reduce"
                ? `${vitSpend} VIT · −${fmt(vitSaveUsd * RATE_DEPO, 0)} HTG sou frè a`
                : `${vitSpend} VIT · +${fmt(boostUsd, 2)} USD sou balans ou` },
        { icon: "flag", k: "Tip", v: "Rechaj Balans" },
        { icon: "card", k: "Destinasyon", v: "Balans Payvit ou" },
        { icon: "wallet", k: "Total ou ap peye", v: `${fmt(typed + feeAfter * RATE_DEPO, 0)} HTG` },
        { div: true },
        { icon: "arrowDown", k: "Yo resevwa", v: `${fmt(credited, 2)} USD`, tone: "ok" },
    ]

    /** Bounds match /depot/moncash|natcash's own validation exactly — catching
     *  it here means a friendlier message than a raw 422 round trip. */
    const DEPOT_HTG_MIN = 680
    const DEPOT_HTG_MAX = 20400

    const idemRef = React.useRef<string | null>(null)

    const submit = async () => {
        if (typed < DEPOT_HTG_MIN || typed > DEPOT_HTG_MAX) {
            say(`Kantite a dwe ant ${fmt(DEPOT_HTG_MIN, 0)} ak ${fmt(DEPOT_HTG_MAX, 0)} HTG.`)
            return
        }
        setBusy(true)
        const token = await getAccessToken()
        if (!token) {
            setBusy(false)
            say("Sesyon ekspire — konekte ankò.")
            return
        }
        if (!idemRef.current) idemRef.current = generateIdempotencyKey()
        // NOTE: /depot/moncash and /depot/natcash don't accept idempotency_key
        // today (unlike the /transfer/* endpoints) — kept here only so a
        // retry after a network error is at least distinguishable client-side
        // if the backend adds support later; harmless to send either way
        // since the backend simply ignores unknown fields.
        const result = await submitDeposit(from, {
            montant_htg: typed,
            txn_id: verify.hasTxn ? verify.txn : undefined,
            proof_image: verify.proof?.dataUrl,
        }, token)

        setBusy(false)
        if (!result.ok) {
            say(result.error ?? "Erè. Eseye ankò.")
            return
        }
        idemRef.current = null
        onSubmitted({
            id: `top-${Date.now().toString(36)}`, seed: USER.seed, kind: "depo",
            name: "Rechaj Balans Payvit", handle: from, method: from,
            nit: result.nit ?? "—",
            amount: credited, statut: "en_attente", at: "Kounye a", fee: feeAfter, rate: RATE_DEPO,
        } as Tx)
        verify.reset()
        setStep(0)
    }

    return (
        <>
            <StepRail step={step} />
            {/* Keyed remount rather than a nested AnimatePresence — see the
                note in Transfe; a second mode="wait" inside the page-level one
                deadlocks the same way here. */}
            <div className="steps-vp">
                <motion.div key={step}
                    initial={reduced ? { opacity: 0 } : { opacity: 0, x: travel * 30 }}
                    animate={{ opacity: 1, x: 0 }} transition={T.base} className="stack">

                    {step === 0 && (
                        <>
                            {/* No in-page title. The header already reads
                                "Rechaje Balans", and repeating it here cost a
                                third of the first screen to say it twice. The
                                Transfè flow opens straight into its first
                                section the same way, so `.stack`'s own gap is
                                the spacing above "Soti nan" — nothing special
                                to compensate for. */}
                            <section className="sec">
                                <h2 className="sec-t">Soti nan</h2>
                                {/* Same ProviderPicker as the converter. Two options, so
                                    one row — the international list is not a top-up
                                    source and is absent rather than present-and-blocked. */}
                                <div className="pv-tc-pickerwrap pv-tc-glass-lite">
                                    <ProviderPicker options={BRIDGE_RAILS} value={from}
                                                    onChange={(v) => { setFrom(v); verify.reset() }} layoutId="rch-pf" />
                                </div>
                            </section>

                            <section className="sec">
                                <h2 className="sec-t">Kantite (HTG)</h2>
                                <div className="chips">
                                    {["1000", "2500", "5000", "10000", "20000"].map((v) => (
                                        <Press key={v} className={`chip g g-chip${htg === v ? " on" : ""}`} onClick={() => setHtg(v)} scale={0.94}>
                                            {htg === v && <motion.span layoutId="rch-pill" className="chip-fill" transition={SPRING} />}
                                            <span style={{ position: "relative", zIndex: 1 }}>{fmt(Number(v), 0)}</span>
                                        </Press>
                                    ))}
                                </div>
                                <div className="field g g-chip">
                                    <Icon name="wallet" size={18} color={C.textMute} />
                                    <input value={htg} onChange={(e) => setHtg(e.target.value.replace(/[^\d.]/g, ""))}
                                           inputMode="decimal" aria-label="Kantite an gourd" />
                                    <span className="field-suffix">HTG</span>
                                </div>
                            </section>

                            <div className="live g g-card">
                                <span className="lbl">Sa w ap resevwa sou balans ou</span>
                                <div className="live-big n">{fmt(credited, 2)}<span> USD</span></div>
                                <div className="live-rows">
                                    <div className="live-row"><span>To</span><span className="n">1 USD = {RATE_DEPO} HTG</span></div>
                                    <div className="live-row"><span>Frè</span><span className="n">{fmt(feeAfter * RATE_DEPO, 0)} HTG</span></div>
                                    <div className="live-row"><span>Destinasyon</span><span>Balans Payvit ou</span></div>
                                    <div className="live-row total"><span>Total ou ap peye</span>
                                        <span className="n">{fmt(typed + feeAfter * RATE_DEPO, 0)} HTG</span></div>
                                </div>
                            </div>

                            <Press className="cta" disabled={!typed} onClick={() => goStep(1)}>
                                Kontinye <Icon name="arrowRight" size={18} color="#fff" stroke={2.2} />
                            </Press>
                        </>
                    )}

                    {/* ── STEP 2 · VERIFYE — the identical gate ────────────
                        `source` is whatever step 1 selected, so the QR box
                        names the account the top-up is actually coming FROM. */}
                    {step === 1 && (
                        <>
                            <PayBrief
                                amount={typed} feeGross={fee * RATE_DEPO}
                                feeNet={feeAfter * RATE_DEPO} cur="HTG" dp={0}
                                vitOn={vitOn} setVitOn={setVitOn} vitSpend={vitSpend} vitCount={vitCount}
                                vitMode={vitMode} setVitMode={setVitMode}
                                boost={boostUsd} boostCur="USD" boostDp={2}
                            />
                            <VerifyBox source={from} v={verify} />
                            <Blocked missing={verify.missing} />
                            <div className="btn-row">
                                <Press className="cta cta-ghost" onClick={() => goStep(0)}>Retounen</Press>
                                <Press className="cta" onClick={() => goStep(2)} disabled={!verify.ok}>
                                    Kontinye <Icon name="arrowRight" size={18} color="#fff" stroke={2.2} />
                                </Press>
                            </div>
                        </>
                    )}

                    {step === 2 && (
                        <KonfimeStep
                            seed={USER.seed} name={USER.name}
                            rows={sumRows} reference={reference}
                            onEdit={() => goStep(0)} onConfirm={submit} busy={busy}
                            say={say} blocked={verify.missing}
                        />
                    )}
                </motion.div>
            </div>
        </>
    )
}

// ═══════════════════════════════════════════════════════════════════════════
// 8 · NOTIFICATIONS — two sections in one panel
//
// Stacked, not tabbed: there are rarely more than a handful of either, and a
// tab hides half the content behind a click for no gain. Transactions first
// because they are the thing a user opens this to check.
// ═══════════════════════════════════════════════════════════════════════════
/** NOTIFIKASYON — its own page, reached from the bell.
 *
 *  A page rather than a dropdown, and NOT a sixth tab: the dock is five icons
 *  around a raised centre button, and notifications do not earn a permanent
 *  slot beside Akèy. Pushed from the bell, like Rechaje is pushed from the
 *  balance card, it costs no nav slot and has room to become a real feed.
 *
 *  Grouped by day, one card, hairline between groups and nothing between rows
 *  inside a group — so the eye reads days as blocks rather than counting
 *  individual dividers. */
interface DerivedNotif {
    id: string
    seed?: string
    icon?: IconName
    badge?: "in" | "out" | "add" | "ok" | "warn"
    title: string
    body?: string
    at: string
    tx?: Tx
}

/** No notifications endpoint exists anywhere in the backend (verified —
 *  there's genuinely nothing to fetch), so this derives real, per-account
 *  notification-shaped entries from data that DOES exist: the account's
 *  own transaction history and current balance. A real user's own past
 *  transactions and balance, reframed as notifications — not fabricated
 *  events about people who don't exist on their account. */
function deriveNotifications(txs: Tx[], balanceUsd: number): DerivedNotif[] {
    return txs.slice(0, 15).map((t) => {
        const isOut = t.kind === "retre" || t.kind === "transfe"
        const who = t.name || t.method
        if (t.statut === "rejete") {
            return { id: `tx-${t.id}`, seed: t.seed, badge: "warn", tx: t, at: t.at,
                title: `Tranzaksyon ${t.nit} rejte`, body: "Tcheke prèv tranzaksyon an." }
        }
        if (t.statut === "en_attente") {
            return { id: `tx-${t.id}`, seed: t.seed, badge: isOut ? "out" : "in", tx: t, at: t.at,
                title: `Tranzaksyon ${fmtUSD(t.amount)} ap tann verifikasyon` }
        }
        return {
            id: `tx-${t.id}`, seed: t.seed, badge: isOut ? "out" : "in", tx: t, at: t.at,
            title: isOut ? `Ou voye ${fmtUSD(t.amount)} bay ${who}` : `${who} voye ${fmtUSD(t.amount)} ba ou`,
        }
    })
}

function NotifRow({ n, onOpenTx }: { n: DerivedNotif; onOpenTx: (t: Tx) => void }) {
    return (
        <Press
            as="div" role={n.tx ? "button" : undefined}
            tabIndex={n.tx ? 0 : undefined}
            className="nt-row" scale={n.tx ? 0.99 : 1}
            onClick={n.tx ? () => onOpenTx(n.tx!) : undefined}
        >
            <span className="nt-ico">
                {n.seed
                    ? <Avatar seed={n.seed} name="" size={44} />
                    : <span className="nt-glyph">
                          <Icon name={n.icon ?? "star"} size={21} color={C.textSub} stroke={1.7} />
                      </span>}
                {n.badge && (
                    <span className={`nt-badge nt-b-${n.badge}`}>
                        <Icon
                            name={n.badge === "in" ? "arrowDown"
                                : n.badge === "out" ? "arrowUp"
                                : n.badge === "add" ? "plus"
                                : n.badge === "ok" ? "check" : "alert"}
                            size={11} color="#fff" stroke={3} />
                    </span>
                )}
            </span>
            <span className="nt-mid">
                <span className="nt-t">{n.title}</span>
                {n.body && <span className="nt-b">{n.body}</span>}
                <span className="nt-at">{n.at}</span>
            </span>
        </Press>
    )
}

function Notifikasyon({ onOpenTx, txs, balanceUsd }: { onOpenTx: (t: Tx) => void; txs: Tx[]; balanceUsd: number }) {
    const reduced = useReduced()
    // A badge means "something arrived you haven't seen" — opening this
    // tab is the actual "seen" moment, not receiving the push itself.
    React.useEffect(() => { clearAppBadge() }, [])
    const lowBalance = balanceUsd > 0 && balanceUsd < 50
    const items = deriveNotifications(txs, balanceUsd)
    // `at` is already "Jodi a · 14:22" / "Yè · 16:12" / "28 jiyè · 08:55" —
    // the part before " · " IS the day bucket, so grouping needs no
    // separate date parsing here.
    const groups: { day: string; items: DerivedNotif[] }[] = []
    for (const n of items) {
        const day = n.at.includes(" · ") ? n.at.split(" · ")[0] : n.at
        let g = groups.find((x) => x.day === day)
        if (!g) { g = { day, items: [] }; groups.push(g) }
        g.items.push(n)
    }

    return (
        <div className="stack">
            <motion.div className="nt-card g g-card"
                initial={reduced ? false : { opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }} transition={T.base}>
                {!lowBalance && groups.length === 0 ? (
                    <div className="empty">
                        <Icon name="bell" size={26} color={C.textMute} />
                        <div>Ou pa gen notifikasyon poko</div>
                    </div>
                ) : (
                    <>
                        {lowBalance && (
                            <NotifRow onOpenTx={onOpenTx} n={{
                                id: "low-balance", icon: "wallet", badge: "add", at: "",
                                title: "Balans ou anba $50.", body: "Panse pou rechaje kont Payvit ou.",
                            }} />
                        )}
                        {groups.map((grp, gi) => (
                            <React.Fragment key={grp.day}>
                                {(gi > 0 || lowBalance) && <div className="nt-sep" />}
                                <div className="nt-day">{grp.day}</div>
                                {grp.items.map((n) => <NotifRow key={n.id} n={n} onOpenTx={onOpenTx} />)}
                            </React.Fragment>
                        ))}
                    </>
                )}
            </motion.div>
        </div>
    )
}


export type TxRoute = "retre" | "depo" | "bridge" | "bridge-intl" | "interne"

const CARDS: {
    key: string
    icon: IconName
    title: string
    sub: string
    rows: { route: TxRoute; icon: IconName; label: string; desc: string }[]
}[] = [
    {
        key: "transfe", icon: "send", title: "Transfè",
        sub: "Voye oswa resevwa lajan ant mwayen ou yo.",
        rows: [
            { route: "retre", icon: "send", label: "Retrè / Transfè",
              desc: "Voye lajan soti nan Balans Payvit ou oswa yon kont entènasyonal." },
            { route: "depo", icon: "download", label: "Depo",
              desc: "Mete lajan nan Balans Payvit ou depi sou MonCash oswa NatCash." },
            { route: "interne", icon: "user", label: "Voye bay yon itilizatè Payvit",
              desc: "Voye dirèkteman bay yon lòt kont Payvit ak imèl li — san frè, imedya." },
        ],
    },
    {
        key: "bridge", icon: "swap", title: "Bridge",
        sub: "Fè lajan w soti sou yon kont ale sou yon lòt.",
        rows: [
            { route: "bridge", icon: "swap", label: "Bridge Lokal",
              desc: "Soti sou MonCash ale sou NatCash, vis e vèsa." },
            { route: "bridge-intl", icon: "globe", label: "Bridge Entènasyonal",
              desc: "Soti sou yon kont entènasyonal ale sou yon lòt." },
        ],
    },
]

/** One card of the chooser.
 *
 *  Depth is derived from the track position rather than from the selected
 *  index, so the neighbour brightens and grows AS the finger moves instead of
 *  waiting for the release and then stepping. That continuity is most of what
 *  "smooth" means here — the previous version held both cards at their resting
 *  opacity through the whole drag and then cut between them. */
function TcCard({
    card, index, active, x, w, reduced, onPick,
}: {
    card: (typeof CARDS)[number]; index: number; active: boolean
    x: MotionValue<number>; w: number; reduced: boolean; onPick: (r: TxRoute) => void
}) {
    const ww = w || 1
    const range = [-(index + 1) * ww, -index * ww, -(index - 1) * ww]
    const opacity = useTransform(x, range, [0.35, 1, 0.35])
    const scale = useTransform(x, range, [0.94, 1, 0.94])
    return (
        <motion.section
            className="tc-card"
            aria-hidden={!active}
            style={reduced ? undefined : { opacity, scale }}
        >
            <span className="tc-tile g g-card">
                <Icon name={card.icon} size={24} color={C.tealDeep} stroke={1.7} />
            </span>
            <h2 className="tc-t">{card.title}</h2>
            <p className="tc-s">{card.sub}</p>
            <div className="tc-rows">
                {card.rows.map((r) => (
                    <Press
                        as="div" role="button" tabIndex={active ? 0 : -1}
                        key={r.route} className="tc-row g g-card" scale={0.985}
                        onClick={() => onPick(r.route)}
                    >
                        <span className="tc-rico"><Icon name={r.icon} size={21} color="#fff" stroke={1.9} /></span>
                        <div className="tc-rmid">
                            <div className="tc-rl">{r.label}</div>
                            <div className="tc-rd">{r.desc}</div>
                        </div>
                        <Icon name="arrowRight" size={20} color={C.teal} stroke={1.9} />
                    </Press>
                ))}
            </div>
        </motion.section>
    )
}

function TypeChooser({ onPick }: { onPick: (r: TxRoute) => void }) {
    const reduced = useReduced()
    const [i, setI] = React.useState(0)
    const viewportRef = React.useRef<HTMLDivElement | null>(null)
    const last = CARDS.length - 1

    /** The track is driven in PIXELS, not percent.
     *
     *  This is what made the old swipe feel broken. Dragging writes `x` as a
     *  pixel number; the rest position was `${-i * 100}%`. Those two units
     *  cannot be interpolated, so every release was a jump from a number to a
     *  string rather than a spring between two positions — the card appeared
     *  to stick to the finger and then teleport.
     *
     *  Measuring the viewport and animating to `-i * w` puts both ends of the
     *  spring in the same unit, which is all it takes for the release to be
     *  continuous. It also lets dragConstraints span the real track, so the
     *  finger moves the cards 1:1 in the middle and only rubber-bands at the
     *  two ends, instead of fighting an elastic pull back to zero the whole
     *  way across. */
    const trackRef = React.useRef<HTMLDivElement | null>(null)
    const [w, setW] = React.useState(0)
    /** Bumped on every release so the settle effect re-runs even when the
     *  release resolves to the card we were already on. */
    const [settleKey, setSettleKey] = React.useState(0)
    const x = useMotionValue(0)

    /** Measured off the TRACK, not the viewport. `.tc-vp` carries negative
     *  margins and matching padding so the cards can bleed to the screen edge,
     *  which means its clientWidth is a bleed-width, not a card width — using
     *  it made every snap land one gutter short of the card it aimed at. The
     *  track is `width:100%` of the content box and the cards are `flex:0 0
     *  100%` of the track, so the track's width IS the step. */
    React.useLayoutEffect(() => {
        const el = trackRef.current
        if (!el) return
        const measure = () => setW(el.clientWidth)
        measure()
        const ro = new ResizeObserver(measure)
        ro.observe(el)
        return () => ro.disconnect()
    }, [])

    const goTo = React.useCallback((n: number) => setI(Math.max(0, Math.min(last, n))), [last])

    /** ONE path to the rest position, driven imperatively.
     *
     *  The `animate` prop cannot do this job on its own. After a drag, `x` has
     *  been written by the gesture, but if the release resolves to the card we
     *  were already on, the prop's target is unchanged — so React re-renders
     *  with an identical value, motion sees no new target, and the track just
     *  stays wherever the finger dropped it. Every drag that did not cross the
     *  threshold left the carousel visibly off its rails.
     *
     *  Animating `x` from an effect keyed on the position AND the width fixes
     *  that (there is always a target to animate to, changed or not) and folds
     *  resize back onto the same path. */
    React.useEffect(() => {
        if (!w) return
        const target = -i * w
        if (reduced) { x.set(target); return }
        const controls = animate(x, target, SPRING)
        return () => controls.stop()
    }, [i, w, reduced, settleKey])

    /** Arrow keys move between cards. Bound to the window rather than the
     *  track so it works before anything inside has been focused. */
    React.useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "ArrowLeft") { goTo(i - 1); }
            else if (e.key === "ArrowRight") { goTo(i + 1); }
        }
        window.addEventListener("keydown", onKey)
        return () => window.removeEventListener("keydown", onKey)
    }, [i, goTo])

    return (
        <div className="tc">
            <div className="tc-vp" ref={viewportRef}>
                <motion.div
                    ref={trackRef}
                    className="tc-track"
                    /* Drag-with-snap. Release projects where the flick would
                       have carried to and snaps to the nearest card from
                       there, so a fast short flick and a slow long drag both
                       land where the hand meant — one rule instead of a
                       distance test and a velocity test disagreeing. */
                    drag={reduced || !w ? false : "x"}
                    dragConstraints={{ left: -last * w, right: 0 }}
                    dragElastic={0.12}
                    dragMomentum={false}
                    onDragEnd={(_e, info) => {
                        if (!w) return
                        /* Where the flick would have coasted to. Rounding that
                           to the nearest card handles a long fling across
                           several; on its own it would also demand a half-card
                           drag to commit a single step, which is stiffer than
                           it should feel — so a short-and-decisive release
                           (about a third of a card) advances one card. */
                        const projected = x.get() + info.velocity.x * 0.14
                        const nearest = Math.round(-projected / w)
                        const from = -i * w
                        const travelled = projected - from
                        const decisive = Math.abs(travelled) > w * 0.32
                        goTo(Math.abs(nearest - i) > 1
                            ? nearest
                            : decisive ? i + (travelled < 0 ? 1 : -1) : i)
                        setSettleKey((k) => k + 1)
                    }}
                    style={{ x, touchAction: "pan-y", willChange: "transform" }}
                >
                    {CARDS.map((c, ci) => (
                        <TcCard key={c.key} card={c} index={ci} active={ci === i} x={x} w={w}
                                reduced={reduced} onPick={onPick} />
                    ))}
                </motion.div>

                {/* Desktop only. Hidden at the ends rather than disabled — a
                    control that cannot do anything is better absent than grey. */}
                {i > 0 && (
                    <Press className="tc-arrow tc-prev g g-card" onClick={() => goTo(i - 1)}
                           scale={0.92} aria-label="Kat anvan an">
                        <Icon name="chevronLeft" size={20} color={C.tealDeep} stroke={2} />
                    </Press>
                )}
                {i < last && (
                    <Press className="tc-arrow tc-next g g-card" onClick={() => goTo(i + 1)}
                           scale={0.92} aria-label="Kat apre a">
                        <Icon name="chevronRight" size={20} color={C.tealDeep} stroke={2} />
                    </Press>
                )}
            </div>

            <div className="tc-dots" role="tablist" aria-label="Chwazi kalite tranzaksyon">
                {CARDS.map((c, n) => (
                    <Press key={c.key} className={`tc-dot${n === i ? " on" : ""}`} onClick={() => goTo(n)}
                           scale={0.9} role="tab" aria-selected={n === i} aria-label={c.title}>
                        {n === i && <motion.span layoutId="tc-dot" className="tc-dotfill" transition={SPRING} />}
                    </Press>
                ))}
            </div>
        </div>
    )
}
